import chroma from 'chroma-js'
import { ClientJS } from 'clientjslite'

import { resolveBodyCopyRange, serializeInlineImages } from '../EmailView/copyInlineImages'
import { isIframeMode } from './iframeMode'
import { resolveNineNative, type NineNativeAPI } from './nineNative'
import {
  type BorderSide,
  resolveBackgroundColor,
  resolveBorderColor,
  extractBorderColorBox,
  mapBoxShadowColors,
} from './shorthandColor'
import {
  getBodyShadowSheet,
  setBodyShadowConvertedCss,
  setBodyShadowDarkCss,
} from './bodyShadowSheet'
import { createSingleUseFunc } from './singleUse'
import { upgradeInsecureUrl } from './upgradeInsecureImg'

// ─────────────────────────────────────────────────────────────────────────────
// Type declarations & global augmentation
// ─────────────────────────────────────────────────────────────────────────────

type NativeReturnCallback = (result: unknown) => void

interface WebKitMessageHandler {
  postMessage: (message: unknown) => void
}

interface WebKitBridge {
  messageHandlers?: {
    nineHandler?: WebKitMessageHandler
    sizeNotification?: WebKitMessageHandler
  }
}

interface ParentLikeWindow extends Window {
  initNative?: () => string | undefined
}

interface ZoomInfo {
  originWidth: number
  originHeight: number
  ratio?: number
}

interface BodyOriginInfo {
  elem: HTMLElement
  width: string
  widthPriority: string
  minWidth: string
  minWidthPriority: string
}

interface MailViewOptions {
  uiBtn?: boolean
  mode?: 'normal' | 'optimized' | 'auto'
}

interface CSSPropInfo {
  type: 0 | 1
  value: string
  number?: number
  unit?: string
  realUnit?: string
  isRel?: boolean
  num?: number
}

interface RuleColorInfo {
  key: string
  value: string
  darkValue: string
  isImportant: boolean
  isBack?: boolean
}

interface CssRuleInfo {
  selector?: string
  type: 1 | 4
  colorInfoList?: RuleColorInfo[]
  queryCondition?: string
  ruleInfoList?: CssRuleInfo[]
}

interface RuleListInfo {
  ruleInfoList: CssRuleInfo[]
}

interface StyleTagInfo {
  originTag: HTMLStyleElement
  ruleInfoList?: CssRuleInfo[]
  darkModeTag: HTMLStyleElement
}

interface DarkModeInfo {
  foreColor: string
  backColor: string
  foreLum?: number
  backLum?: number
}

interface ResizeObserverEntry {
  prevWidth: number
  prevHeight: number
  element: HTMLElement | null
  observer?: ResizeObserver
  resizeTimer?: ReturnType<typeof setTimeout> | null
  calcHeight?: () => void
  start?: (isFirstCall?: boolean) => void
  clear?: () => void
}

// 확대(zoom) 버튼 — 미클릭 상태로 dispose/removeWrapper될 때 click 리스너를 해제할 수 있도록
// 핸들러를 노드에 보관한다. (R20)
type ExpandBtnElement = HTMLElement & {
  __nineExpandHandler?: (e: MouseEvent) => void
}

interface ViewerWebObjLike {
  setOptimizedBtn?: (key: string, value: boolean) => void
}

// Augmented window shape used internally; we cast `window` to it where
// these properties are accessed (avoids merging conflicts with other modules
// that augment Window with concrete `NineNative` / `MailView` shapes).
interface MailViewWindow extends Window {
  docUtil?: DocUtil
  MailView?: typeof MailView
  NineNative?: NineNativeAPI
  // `singleUse` 는 helper/singleUse.ts 가 lazy 초기화하므로 여기서 augment 하지 않음.
  _bodyBgColor?: string | null
  _tmpDarkMode?: { backColor: string; foreColor: string }
  NORMALIZE_MESSAGE_WIDTHS?: boolean
  // dev 전용(OFM-233 Phase 8 dev-emlview 하네스): true 이면 최적화(화면폭 맞춤 이미지 축소)를 끄고
  // 원본 크기로 렌더한다. raw 브라우저 렌더와 이미지 크기를 맞춰 픽셀 비교를 유의미하게 하기 위함.
  // /dev/mailview 페이지만 설정하며 프로덕션에는 설정되지 않는다.
  __devDisableOptimize?: boolean
  webkit?: WebKitBridge
  webObj?: ViewerWebObjLike
  setDarkMode?: (backColor: string, foreColor: string) => void
  isOfficeMailApp?: () => boolean
  nineLog?: (msg: string) => void
  img_find?: () => string[]
  reloadInlineImage?: (cid: string, uri: string) => void
}

const win = window as unknown as MailViewWindow

declare global {
  interface Window {
    docUtil?: DocUtil
    isReactReady?: boolean
    isMailViewReady?: boolean
  }

  interface HTMLElement {
    mvObj?: MailView
    initVisibility?: string
    imgListener?: (e: Event) => void
    imgListenerAS?: (e: Event) => void
    zoomInfo?: ZoomInfo
    // <style>/<link> 에 캐시해 둔 다크 변환 결과 <style> 태그 — 본문 bake 마다 재파싱을 피한다.
    darkModeElem?: HTMLStyleElement
  }

  interface HTMLImageElement {
    initVisibility?: string
    imgListener?: (e: Event) => void
    imgListenerAS?: (e: Event) => void
  }
}

// iframe 컨텍스트 판별 — `isIframeMode()` 의 두 컨텍스트 (iframe / inner) 의미는
// helper/iframeMode.ts 의 doc 주석 참조. 모듈 평가 시 1 회 캡처 (런타임 중 불변).
const isIframe = isIframeMode()

// partialZoom 폭 비교의 Safari scrollWidth 보정 버퍼 (OMD-239).
// Safari(WebKit)는 가로 스크롤이 불가능한 상황(문자열만 든 block 요소 등)에서도 scrollWidth 를
// offsetWidth/clientWidth(및 Math.ceil(getBoundingClientRect().width)) 보다 1px 크게 측정하는
// 고질적 레이아웃 버그가 있다. 실측(실제 Safari, 601개 폭)상 scrollWidth 가 ceil(rect.width)+1 이
// 되는 경우가 약 19% 발생해, 이 1px 로 불필요한 nf-zoom 래핑이 생긴다. "실제로 넘치는지" 를
// 1px 미만 차이로 정확히 구분할 방법이 없으므로, Safari 에서만 가용 폭 쪽에 +1 여유를 둬서
// (scrollWidth 가 가용폭+1 을 "넘어야" 래핑) 오동작을 막는다. 실제 overflow 대상은 +3px 이상
// 초과하므로 영향받지 않는다. Chrome 등은 버퍼 0.
const SAFARI_SCROLLW_BUFFER = new ClientJS().isSafari() ? 1 : 0

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// Static-template HTML assignment helper (no user input — XSS-safe).
function setHTML(el: Element, html: string): void {
  Reflect.set(el, 'innerHTML', html)
}

function getCSSProp(str: string, isAttr?: boolean): CSSPropInfo {
  const matchList = str?.match(/^(-{0,1}\d+[.]*\d*|\d*[.]*\d+)/)
  if (matchList != null && matchList.length > 0) {
    const numStr = matchList[0]
    let unit = str.substring(numStr.length)
    let num = numStr.indexOf('.') > -1 ? parseFloat(numStr) : parseInt(numStr, 10)
    switch (unit) {
      case 'pt':
        num = (num * 4) / 3
        unit = 'px'
        break
      case 'in':
        num = num * 96
        unit = 'px'
        break
      case 'pc':
        num = num * 16
        unit = 'px'
        break
      case 'cm':
        num = num * 37.79
        unit = 'px'
        break
      case 'mm':
        num = num * 3.779
        unit = 'px'
        break
    }
    if (isAttr === true && unit === '') {
      unit = 'px'
    }
    return {
      type: 1,
      number: num,
      num,
      unit: unit.length === 0 ? 'px' : unit,
      realUnit: unit,
      isRel: unit !== 'px',
      value: str,
    }
  }
  return { type: 0, value: str }
}

function printLog(...args: unknown[]): void {
  console.log(...args)
}

function createTag(tagText: string): HTMLElement {
  const divElem = document.createElement('div')
  setHTML(divElem, tagText)
  return divElem.firstElementChild as HTMLElement
}

function callWebView(param: string): void {
  printLog('call webview - ' + param)
  if (win.isOfficeMailApp?.() === true) {
    location.href = param
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// :scope selector polyfill (applied once at module load)
// ─────────────────────────────────────────────────────────────────────────────

;(function (doc: Document, proto: Element) {
  try {
    doc.querySelector(':scope body')
  } catch {
    ;(['querySelector', 'querySelectorAll'] as const).forEach((method) => {
      const protoRecord = proto as unknown as Record<string, Function>
      const nativ = protoRecord[method]
      protoRecord[method] = function (this: Element, selectors: string) {
        if (/(^|,)\s*:scope/.test(selectors)) {
          const id = this.id
          this.id = 'ID_' + new Date().getTime()
          const replaced = selectors.replace(/((^|,)\s*):scope/g, '$1#' + this.id)
          const result = (doc as unknown as Record<string, Function>)[method].call(doc, replaced)
          this.id = id
          return result
        }
        return nativ.call(this, selectors)
      }
    })
  }
})(window.document, Element.prototype as unknown as Element)

// ─────────────────────────────────────────────────────────────────────────────
// EditorMgr — color management, native bridge, dark-mode color computations
// ─────────────────────────────────────────────────────────────────────────────

// bodyIsolation 'shadow-dom': 본문 <style>/<link> 는 .htmlMailBody host 의 shadowRoot 안에 있어
// querySelectorAll 이 경계를 넘지 못한다. root 와 그 아래 본문 shadowRoot 들을 함께 조회한다.
function querySelectorAllComposed<T extends Element = Element>(
  root: Document | HTMLElement,
  selector: string,
): T[] {
  const found = Array.from(root.querySelectorAll<T>(selector))
  const hosts = Array.from(root.querySelectorAll<HTMLElement>('.htmlMailBody'))
  if (root instanceof HTMLElement && root.classList.contains('htmlMailBody')) hosts.unshift(root)
  for (const host of hosts) {
    if (host.shadowRoot != null)
      found.push(...Array.from(host.shadowRoot.querySelectorAll<T>(selector)))
  }
  return found
}

// _checkElemColor 가 style 외에 색 속성을 읽는 태그(FONT color, table 계열·BODY·MARQUEE bgcolor).
const COLOR_ATTR_TAGS = new Set([
  'FONT',
  'BODY',
  'MARQUEE',
  'TABLE',
  'THEAD',
  'TBODY',
  'TFOOT',
  'TR',
  'TH',
  'TD',
])

export class EditorMgr {
  darkModeInfo: DarkModeInfo = {
    foreColor: '#ffffff',
    backColor: '#000000',
  }

  checkETVSelector: string[] | null = null

  private _linkColor = {
    link: 'rgb(0, 0, 238)',
    visited: 'rgb(67, 0, 120)',
  }

  private _foreColorMap: Record<string, string> = {}
  private _backColorMap: Record<string, string> = {}
  private _foreColorRevMap: Record<string, string> = {}
  private _backColorRevMap: Record<string, string> = {}

  // 본문 복사 시 인라인 이미지(blob:/officemail-inline://)→data: 변환 캐시(SSOT, §15.14-②).
  // 생산자(EmailView.ensureInlineDataUrls)가 선변환 완료 시 push, 소비자(DocUtil.processCopy)가
  // copy 시 동기 조회. 키=참조 url, 값=data url. DocUtil 재생성 시 EditorMgr 도 재생성돼 초기화.
  inlineDataUrlMap: Record<string, string> = {}

  private _styleTagList: StyleTagInfo[] = []
  private _createInvitationTimer: ReturnType<typeof setTimeout> | null = null

  constructor(private docUtil: DocUtil) {}

  createSingleUseFunc(cb: NativeReturnCallback): string {
    // helper/singleUse.ts 가 owner — iframe / non-iframe 양쪽 컨텍스트를 일관되게 처리.
    return createSingleUseFunc(cb as (param: unknown) => void)
  }

  isPromise(value: unknown): value is Promise<unknown> {
    return value != null && typeof (value as { then?: unknown }).then === 'function'
  }

  nativeCall(
    funcName: string,
    returnCallback: NativeReturnCallback | null,
    paramArray?: unknown[],
  ): boolean {
    const nine = resolveNineNative()
    if (nine != null) {
      try {
        const fn = nine[funcName]
        if (fn != null) {
          const ret = fn.apply(nine, paramArray ?? [])
          if (returnCallback != null) {
            if (this.isPromise(ret)) {
              ret.then((result) => returnCallback(result))
            } else {
              returnCallback(ret)
            }
          }
          return true
        }
      } catch (e) {
        console.log(e)
      }
    } else if (win.webkit?.messageHandlers?.nineHandler?.postMessage) {
      let returnFuncName = ''
      if (returnCallback != null) {
        returnFuncName = this.createSingleUseFunc(returnCallback)
      }
      const param: { function: string; returnFunction: string; params?: unknown[] } = {
        function: funcName,
        returnFunction: returnFuncName,
      }
      if (paramArray != null && paramArray.length > 0) {
        param.params = paramArray
      }
      win.webkit.messageHandlers.nineHandler.postMessage(JSON.stringify(param))
      return true
    }
    return false
  }

  notifyCreateInvitation(startDateStr: string, endDateStr: string, displayStr: string): void {
    if (this._createInvitationTimer != null) {
      clearTimeout(this._createInvitationTimer)
    }
    this._createInvitationTimer = setTimeout(() => {
      console.log(' - notifyCreateInvitation - ', startDateStr, endDateStr, displayStr)
      this.nativeCall('createInvitation', null, [startDateStr, endDateStr, displayStr])
      this._createInvitationTimer = null
    }, 300)
  }

  _getHexColor(colStr: string): string {
    let crm: chroma.Color
    try {
      crm = chroma(colStr)
    } catch {
      if (colStr.toLowerCase() === 'transparent') return '#ffffff00'
      return '#808080'
    }
    return crm.hex()
  }

  // `var(key, fallback)` / `var(key)` 를 파싱한다. 순수 var() 표현식이 아니면(뒤에 다른 토큰이
  // 붙거나 형식이 아니면) null. fallback 은 최상위(depth 0) 첫 콤마로 분리해 중첩 rgb()/var() 의
  // 내부 콤마에 안전하다.
  private _parseCssVar(colStr: string): { key: string; fallback: string | null } | null {
    const s = colStr.trim()
    if (!/^var\s*\(/i.test(s) || !s.endsWith(')')) return null
    const open = s.indexOf('(')
    // 여는 'var(' 의 짝 ')' 가 문자열 끝인지 확인 — 아니면 순수 var() 가 아니다.
    let depth = 0
    let closeIdx = -1
    for (let i = open; i < s.length; i++) {
      if (s[i] === '(') depth++
      else if (s[i] === ')') {
        depth--
        if (depth === 0) {
          closeIdx = i
          break
        }
      }
    }
    if (closeIdx !== s.length - 1) return null
    const inner = s.slice(open + 1, closeIdx)
    // 최상위 첫 콤마로 key | fallback 분리
    depth = 0
    for (let i = 0; i < inner.length; i++) {
      const ch = inner[i]
      if (ch === '(') depth++
      else if (ch === ')') depth--
      else if (ch === ',' && depth === 0) {
        return { key: inner.slice(0, i).trim(), fallback: inner.slice(i + 1).trim() }
      }
    }
    return { key: inner.trim(), fallback: null }
  }

  // colStr 이 색상값(또는 다크 변환 가능한 중첩 var())인지 판별한다.
  private _isColorLike(colStr: string): boolean {
    const s = colStr.trim()
    if (s === '') return false
    if (s.toLowerCase() === 'transparent') return true
    if (this._parseCssVar(s) != null) return true
    try {
      chroma(s)
      return true
    } catch {
      return false
    }
  }

  _getDarkModeColor(_elem: HTMLElement | null, colStr: string, isBack: boolean): string | null {
    // var(key, fallback) 형식은 CSS 변수 참조(key)는 그대로 두고 fallback(색상 또는 중첩 var)만
    // 다크 변환하여 var(key, <darkColor>) 로 되돌린다. fallback 이 없거나 색상이 아니면 원본 유지.
    const cssVar = this._parseCssVar(colStr)
    if (cssVar != null) {
      if (cssVar.fallback == null || !this._isColorLike(cssVar.fallback)) return colStr
      const darkFallback = this._getDarkModeColor(_elem, cssVar.fallback, isBack) ?? cssVar.fallback
      return `var(${cssVar.key}, ${darkFallback})`
    }

    let crm: chroma.Color
    let alpha = 1
    try {
      crm = chroma(colStr)
      alpha = crm.alpha()
    } catch {
      // chroma 가 파싱하지 못하는 값(currentcolor·inherit·unset·transparent 등)은 변환하지 않고
      // null 을 반환한다. 호출부는 null 이면 원본을 보존한다(강제로 흰/검 덮어쓰지 않음).
      return null
    }

    let col = crm
    const lum = crm.luminance()
    let newLum: number

    if (lum === 0) {
      col = chroma(this.darkModeInfo.foreColor)
      if (col.alpha() === 0) col = col.alpha(1)
    } else if (lum === 1) {
      col = chroma(this.darkModeInfo.backColor)
      if (col.alpha() === 0) col = col.alpha(1)
      if (isBack === true) return col.hex()
    } else if (isBack === true) {
      if (lum < 0.01) {
        col = crm
      } else if (lum < 0.1) {
        col = crm.luminance(lum + 0.1)
      } else if (lum < 0.2) {
        col = crm.luminance(lum + 0.2)
      } else if (lum > 0.75) {
        newLum = 1 - (this.darkModeInfo.backLum ?? 0) - lum
        newLum = Math.max(newLum, 0)
        col = crm.luminance(newLum)
      }
    } else {
      if (lum < 0.2) {
        col = crm.luminance(0.5 - lum)
      } else if (lum > 0.9) {
        col = crm.luminance(1.0 - lum)
      } else if (lum > 0.75) {
        newLum = 1 - (this.darkModeInfo.backLum ?? 0) - lum
        newLum = Math.max(newLum, 0)
        col = crm.luminance(newLum)
      }
    }

    col = col.alpha(alpha)
    return col.hex()
  }

  _checkElemColor(elem: HTMLElement | null, targetCol?: string): void {
    if (this._foreColorMap['#000000'] == null) {
      const darkCol = this._getDarkModeColor(null, '#000000', false)
      // '#000000' 은 항상 유효 색이라 null 이 될 수 없으나 nullable 시그니처 타입을 좁힌다.
      if (darkCol != null) {
        this._foreColorMap['#000000'] = darkCol
        this._foreColorRevMap[darkCol] = 'rgb(0, 0, 0)'
        this._backColorMap['#000000'] = darkCol
        this._backColorRevMap[darkCol] = 'rgb(0, 0, 0)'
      }
    }

    // style 속성도 색 속성(color/bgcolor)을 읽는 태그도 아니면 아래 판정은 모두 빈 값이라 변환할 것이
    // 없다 — 대형 본문(요소 20만)에서 요소마다 style 열거·shorthand 복구를 건너뛰고 자식만 순회한다.
    if (elem != null && (elem.hasAttribute('style') || COLOR_ATTR_TAGS.has(elem.tagName))) {
      // Array.from(style) 는 pending-substitution(var in shorthand) 판정에 쓰이며, background·4면
      // border 가 공유하도록 요소당 1회만 생성한다(§5.1).
      const styleList = Array.from(elem.style)
      const foreColor = elem.style.color
      const backColor = resolveBackgroundColor(elem.style, styleList)
      let newCol: string | null = null

      if (
        foreColor != null &&
        foreColor.length > 0 &&
        (targetCol === this._getHexColor(foreColor) || elem.getAttribute('data-nine-sc') == null)
      ) {
        newCol = this._getDarkModeColor(elem, foreColor, false)
        if (newCol != null && newCol !== foreColor) {
          elem.style.color = newCol
          this._foreColorMap[foreColor] = elem.style.color
          this._foreColorRevMap[newCol] = foreColor
          elem.setAttribute('data-nine-sc', foreColor)
        }
      }
      if (
        backColor != null &&
        backColor.length > 0 &&
        (targetCol === this._getHexColor(backColor) || elem.getAttribute('data-nine-sb') == null)
      ) {
        newCol = this._getDarkModeColor(elem, backColor, true)
        if (newCol != null && newCol !== backColor) {
          elem.style.backgroundColor = newCol
          this._backColorMap[backColor] = elem.style.backgroundColor
          this._backColorRevMap[newCol] = backColor
          elem.setAttribute('data-nine-sb', backColor)
        }
      }
      if (elem.tagName === 'FONT') {
        const fontColor = elem.getAttribute('color')
        if (
          fontColor != null &&
          fontColor.length > 0 &&
          (targetCol === this._getHexColor(fontColor) || elem.getAttribute('data-nine-ac') == null)
        ) {
          newCol = this._getDarkModeColor(elem, fontColor, false)
          if (newCol != null && newCol !== fontColor) {
            elem.setAttribute('color', newCol)
            this._foreColorMap[fontColor] = elem.getAttribute('color') ?? ''
            this._foreColorRevMap[newCol] = fontColor
            elem.setAttribute('data-nine-ac', fontColor)
          }
        }
      }
      if (
        elem.tagName === 'BODY' ||
        elem.tagName === 'MARQUEE' ||
        elem.tagName === 'TABLE' ||
        elem.tagName === 'THEAD' ||
        elem.tagName === 'TBODY' ||
        elem.tagName === 'TFOOT' ||
        elem.tagName === 'TR' ||
        elem.tagName === 'TH' ||
        elem.tagName === 'TD'
      ) {
        let bgColor = elem.getAttribute('bgcolor')
        // 루트 표면(inner=rootContainer / iframe=doc.body 폴백)일 때 저장된 본문 배경색을 사용(§15.13).
        if (elem === this.docUtil.container && win._bodyBgColor != null) {
          bgColor = win._bodyBgColor
        }
        if (backColor == null || backColor.length === 0) {
          if (
            bgColor != null &&
            bgColor.length > 0 &&
            (targetCol === this._getHexColor(bgColor) || elem.getAttribute('data-nine-ab') == null)
          ) {
            newCol = this._getDarkModeColor(elem, bgColor, true)
            if (newCol != null && newCol !== bgColor) {
              elem.removeAttribute('bgcolor')
              elem.style.backgroundColor = newCol
              this._backColorMap[bgColor] = elem.style.backgroundColor
              this._backColorRevMap[newCol] = bgColor
              elem.setAttribute('data-nine-ab', bgColor)
            }
          }
        } else if (
          bgColor != null &&
          bgColor.length > 0 &&
          (targetCol === this._getHexColor(bgColor) || elem.getAttribute('data-nine-ab') == null)
        ) {
          newCol = this._getDarkModeColor(elem, bgColor, true)
          if (newCol != null && newCol !== bgColor) {
            elem.setAttribute('bgcolor', newCol)
            this._foreColorMap[bgColor] = elem.getAttribute('bgcolor') ?? ''
            this._foreColorRevMap[newCol] = bgColor
            elem.setAttribute('data-nine-ab', bgColor)
          }
        }
      }

      // border color 는 shorthand(var 포함)로 들어오면 longhand 가 pending 이라 getPropertyValue 가 ""
      // 가 된다. width 가드 없이 resolveBorderColor 로 원본을 복구해 판정한다(§5.1). border-color box 는
      // 요소당 1회만 전개해 4면이 공유한다.
      const borderSides: BorderSide[] = ['top', 'left', 'right', 'bottom']
      const borderColorProps = [
        'borderTopColor',
        'borderLeftColor',
        'borderRightColor',
        'borderBottomColor',
      ]
      const borderAttrs = ['btc', 'blc', 'brc', 'bbc']
      const borderColorBox = extractBorderColorBox(elem.style.getPropertyValue('border-color'))

      borderSides.forEach((side, idx) => {
        const col = resolveBorderColor(elem.style, side, borderColorBox, styleList)
        if (col == null || col.length === 0) return
        if (
          targetCol !== this._getHexColor(col) &&
          elem.getAttribute('data-nine-' + borderAttrs[idx]) != null
        ) {
          return
        }
        newCol = this._getDarkModeColor(elem, col, false)
        if (newCol == null || newCol === col) return
        const styleRecord = elem.style as unknown as Record<string, string>
        styleRecord[borderColorProps[idx]] = newCol
        this._foreColorMap[col] = styleRecord[borderColorProps[idx]]
        this._foreColorRevMap[newCol] = col
        elem.setAttribute('data-nine-' + borderAttrs[idx], col)
      })
    }

    if (elem != null) {
      for (let i = 0; i < elem.children.length; i++) {
        this._checkElemColor(elem.children[i] as HTMLElement, targetCol)
      }
      // bodyIsolation 'shadow-dom': 본문은 .htmlMailBody host 의 shadowRoot 안에 있다.
      const shadowChildren = elem.classList.contains('htmlMailBody')
        ? elem.shadowRoot?.children
        : null
      if (shadowChildren != null) {
        for (let i = 0; i < shadowChildren.length; i++) {
          this._checkElemColor(shadowChildren[i] as HTMLElement, targetCol)
        }
      }
    }
  }

  _restoreElemColor(elem: HTMLElement): void {
    const sc = elem.getAttribute('data-nine-sc')
    const sb = elem.getAttribute('data-nine-sb')
    const ac = elem.getAttribute('data-nine-ac')
    const ab = elem.getAttribute('data-nine-ab')
    const btc = elem.getAttribute('data-nine-btc')
    const blc = elem.getAttribute('data-nine-blc')
    const brc = elem.getAttribute('data-nine-brc')
    const bbc = elem.getAttribute('data-nine-bbc')
    let cssChanged = false

    if (sc != null) {
      if (this._foreColorMap[sc] === elem.style.color) {
        elem.style.color = sc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-sc')
    }
    if (sb != null) {
      if (this._backColorMap[sb] === elem.style.backgroundColor) {
        elem.style.backgroundColor = sb
        cssChanged = true
      }
      elem.removeAttribute('data-nine-sb')
    }
    if (ac != null) {
      if (this._foreColorMap[ac] === elem.getAttribute('color')) {
        elem.setAttribute('color', ac)
      }
      elem.removeAttribute('data-nine-ac')
    }
    if (ab != null) {
      const bgColor = elem.getAttribute('bgcolor')
      if (bgColor == null) {
        if (this._backColorMap[ab] === elem.style.backgroundColor) {
          elem.style.backgroundColor = ''
          elem.setAttribute('bgcolor', ab)
          cssChanged = true
        }
      } else if (this._backColorMap[ab] === bgColor) {
        elem.setAttribute('bgcolor', ab)
      }
      elem.removeAttribute('data-nine-ab')
    }
    const savedMap = this._foreColorMap
    if (btc != null) {
      if (savedMap[btc] === elem.style.borderTopColor) {
        elem.style.borderTopColor = btc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-btc')
    }
    if (blc != null) {
      if (savedMap[blc] === elem.style.borderLeftColor) {
        elem.style.borderLeftColor = blc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-blc')
    }
    if (brc != null) {
      if (savedMap[brc] === elem.style.borderRightColor) {
        elem.style.borderRightColor = brc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-brc')
    }
    if (bbc != null) {
      if (savedMap[bbc] === elem.style.borderBottomColor) {
        elem.style.borderBottomColor = bbc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-bbc')
    }

    if (cssChanged === true) {
      const styleAttr = elem.getAttribute('style')
      if (styleAttr != null) elem.setAttribute('style', styleAttr)
    }

    for (let i = 0; i < elem.children.length; i++) {
      this._restoreElemColor(elem.children[i] as HTMLElement)
    }
    const shadowChildren = elem.classList.contains('htmlMailBody')
      ? elem.shadowRoot?.children
      : null
    if (shadowChildren != null) {
      for (let i = 0; i < shadowChildren.length; i++) {
        this._restoreElemColor(shadowChildren[i] as HTMLElement)
      }
    }
  }

  // 순서로 background 색상인지 구분해야 하므로, 순서를 잘 관리해야 함.
  // box-shadow 는 색 longhand 가 아니라 토큰 나열(offset·blur·spread·color·inset)이라 darkValue 계산만
  // 다르게(토큰별 변환) 처리되며, 첫 요소(back)가 아니어야 하므로 반드시 맨 뒤에 둔다.
  _colorPropList: string[] = [
    'background-color',
    'color',
    'border-color',
    'border-top-color',
    'border-bottom-color',
    'border-left-color',
    'border-right-color',
    'box-shadow',
  ]

  // border-{side}-color longhand → BorderSide. resolver 로 pending 값을 복구할 때 사용.
  private readonly _borderColorSide: Record<string, BorderSide> = {
    'border-top-color': 'top',
    'border-bottom-color': 'bottom',
    'border-left-color': 'left',
    'border-right-color': 'right',
  }

  _processSheet(sheet: CSSStyleSheet | CSSMediaRule | null): RuleListInfo {
    const ruleListInfo: RuleListInfo = { ruleInfoList: [] }
    if (sheet != null) {
      // cross-origin <link> 시트는 cssRules 접근 자체가 SecurityError 를 던진다 — 읽을 수 없는
      // 시트는 조용히 skip 한다(동일-origin <style>/<link> 만 변환 대상).
      let rules: CSSRuleList
      try {
        rules = sheet.cssRules
      } catch {
        return ruleListInfo
      }
      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i]
        if (rule instanceof CSSMediaRule) {
          const mqRes = this._processSheet(rule)
          if (mqRes.ruleInfoList.length > 0) {
            ruleListInfo.ruleInfoList.push({
              queryCondition: rule.media.mediaText,
              ruleInfoList: mqRes.ruleInfoList,
              type: 4,
            })
          }
        } else if (rule instanceof CSSStyleRule) {
          if (
            this.checkETVSelector == null ||
            this.checkETVSelector.some((selector) => rule.selectorText.indexOf(selector) === 0)
          ) {
            const ruleInfo: CssRuleInfo = { selector: rule.selectorText, type: 1 }
            // pending-substitution 판정용 Array.from 과 border-color box 는 규칙당 1회만 계산해 공유한다.
            const ruleStyleList = Array.from(rule.style)
            const ruleBorderColorBox = extractBorderColorBox(
              rule.style.getPropertyValue('border-color'),
            )
            this._colorPropList.forEach((prop, idx) => {
              const side = this._borderColorSide[prop]
              let value: string
              if (prop === 'background-color') {
                value = resolveBackgroundColor(rule.style, ruleStyleList) ?? ''
              } else if (side != null) {
                value =
                  resolveBorderColor(rule.style, side, ruleBorderColorBox, ruleStyleList) ?? ''
              } else {
                value = rule.style.getPropertyValue(prop)
              }
              if (
                value.length > 0 &&
                value !== 'inherit' &&
                value !== 'initial' &&
                value !== 'unset'
              ) {
                const isBack = idx < 1 // _colorPropList 의 첫번째만 back 색상에 해당.
                let darkValue: string | null
                if (prop === 'box-shadow') {
                  // box-shadow 는 값 전체가 색이 아니라 토큰 나열이므로, 토큰별로 다크 변환하고
                  // 비색 토큰(offset·blur·spread·inset)은 보존한다(색 판별은 _getDarkModeColor 의 null 위임).
                  darkValue = mapBoxShadowColors(value, (token) =>
                    this._getDarkModeColor(null, token, false),
                  )
                  // 변환된 색이 하나도 없으면(none 포함) 원본과 동일 → 중복 출력 skip.
                  if (darkValue === value) return
                } else {
                  darkValue = this._getDarkModeColor(null, value, isBack)
                  // 변환 불가(null) 색은 출력 목록에서 제외해 'null' 직렬화를 막는다.
                  if (darkValue == null) return
                }
                const isImportant = rule.style.getPropertyPriority(prop) === 'important'
                const colorInfo: RuleColorInfo = {
                  key: prop,
                  value,
                  darkValue,
                  isImportant,
                  isBack,
                }
                if (!ruleInfo.colorInfoList) {
                  ruleInfo.colorInfoList = []
                }
                ruleInfo.colorInfoList.push(colorInfo)
              }
            })

            if (ruleInfo.colorInfoList && ruleInfo.colorInfoList.length > 0) {
              ruleListInfo.ruleInfoList.push(ruleInfo)
            }
          }
        }
      }
    }
    return ruleListInfo
  }

  private _getIndent(depth: number): string {
    let str = ''
    for (let i = 0; i < depth; i++) str += '  '
    return str
  }

  private _makeCssText(ruleInfo: CssRuleInfo, indent: number): string {
    let str = ''
    if (ruleInfo.type === 1) {
      str += this._getIndent(indent) + ruleInfo.selector + ' {\n'
      if (ruleInfo.colorInfoList != null) {
        ruleInfo.colorInfoList.forEach((colorInfo) => {
          const colorValue = colorInfo.darkValue
          str +=
            this._getIndent(indent + 1) +
            colorInfo.key +
            ': ' +
            colorValue +
            (colorInfo.isImportant !== true ? '' : ' !important') +
            ';\n'
        })
      }
      str += this._getIndent(indent) + '}\n'
    } else if (ruleInfo.type === 4) {
      const darkSchemeRegex = /prefers-color-scheme[ ]?:/i
      if (
        ruleInfo.queryCondition != null &&
        darkSchemeRegex.test(ruleInfo.queryCondition) !== true
      ) {
        str += this._getIndent(indent) + '@media ' + ruleInfo.queryCondition + ' {\n'
        const ruleText = this._getStyleText(ruleInfo.ruleInfoList ?? [], indent + 1)
        str += ruleText
        str += this._getIndent(indent) + '}\n'
      }
    }
    return str
  }

  private _getStyleText(ruleInfoList: CssRuleInfo[], indent: number): string {
    let str = ''
    for (let i = 0; i < ruleInfoList.length; i++) {
      str += this._makeCssText(ruleInfoList[i], indent)
    }
    return str
  }

  // origin <style>/<link> 시트를 다크 변환해 바로 뒤에 <style data-nine-style> 복제 태그를 삽입한다.
  // 읽기 불가(cross-origin)·변환 대상 없음이면 no-op. 삽입 태그는 _restoreStyleTagCss 의 속성 쿼리로
  // 제거되며, _styleTagList 에도 추적해 target 없는 restore 경로에서도 제거되게 한다.
  private _appendDarkTagForSheet(originEl: HTMLStyleElement | HTMLLinkElement): void {
    // 이미 이 시트로 만들어 둔 다크 태그가 있으면 재파싱 없이 재삽입만 한다. restore 는 DOM 에서
    // 제거(removeChild)만 하고 이 참조는 유지되므로, detached 된 태그를 다시 붙이면 된다. 정적
    // 앱/chrome 시트를 본문 bake 마다 다시 파싱하는 비용을 없앤다.
    if (originEl.darkModeElem != null) {
      originEl.parentElement?.insertBefore(originEl.darkModeElem, originEl.nextSibling)
      this._styleTagList.push({ originTag: originEl, darkModeTag: originEl.darkModeElem })
      return
    }
    const sheetInfo = this._processSheet(originEl.sheet)
    if (sheetInfo.ruleInfoList.length === 0) return
    const cssText = '\n' + this._getStyleText(sheetInfo.ruleInfoList, 1)
    if (cssText.trim().length === 0) return
    const newStyleTag = document.createElement('style')
    newStyleTag.setAttribute('data-nine-style', 'true')
    setHTML(newStyleTag, cssText)
    originEl.parentElement?.insertBefore(newStyleTag, originEl.nextSibling)
    originEl.darkModeElem = newStyleTag
    this._styleTagList.push({
      originTag: originEl,
      ruleInfoList: sheetInfo.ruleInfoList,
      darkModeTag: newStyleTag,
    })
  }

  _checkStyleTagCss(target?: Document | HTMLElement | null): void {
    this.darkModeInfo.foreLum = chroma(this.darkModeInfo.foreColor).luminance()
    this.darkModeInfo.backLum = chroma(this.darkModeInfo.backColor).luminance()

    const doc = target || document
    this._styleTagList = []

    const styleTagList = querySelectorAllComposed<HTMLStyleElement>(
      doc,
      'style:not(#darkmode-info):not(#ios-css)',
    )
    for (let i = 0; i < styleTagList.length; i++) {
      this._appendDarkTagForSheet(styleTagList[i])
    }

    querySelectorAllComposed<HTMLLinkElement>(doc, 'link[rel="stylesheet"][href]').forEach(
      (linkElem) => {
        if (linkElem.sheet == null) {
          // 아직 로드 전인 <link> — 로드 완료 후 1회만 처리한다. 이미 리스너를 단 링크는 skip(중복 방지).
          if (linkElem.dataset.nineLinkPending === 'true') return
          linkElem.dataset.nineLinkPending = 'true'
          linkElem.addEventListener(
            'load',
            () => {
              linkElem.removeAttribute('data-nine-link-pending')
              // 로드 도중 라이트로 꺼졌으면(레이스) orphan 다크 태그를 만들지 않는다.
              if (this.docUtil.isDarkMode() !== true) return
              this._appendDarkTagForSheet(linkElem)
            },
            { once: true },
          )
        } else {
          this._appendDarkTagForSheet(linkElem)
        }
      },
    )
    this._convertBodyShadowSheet()
  }

  // bodyIsolation 'shadow-dom': 본문 기능 규칙(bodyShadowSheet)은 adoptedStyleSheets 라 위 <style> walk 에 잡히지
  // 않는다 — 같은 변환으로 다크판을 만들어 shadow 에 건다. 규칙이 `.etv-container` 로 시작하지 않으므로
  // checkETVSelector 필터는 이 sheet 에만 끈다.
  private _convertBodyShadowSheet(): void {
    const sheet = getBodyShadowSheet()
    if (sheet == null) return
    const saved = this.checkETVSelector
    this.checkETVSelector = null
    try {
      setBodyShadowConvertedCss(this._getStyleText(this._processSheet(sheet).ruleInfoList, 0))
    } finally {
      this.checkETVSelector = saved
    }
  }

  _restoreStyleTagCss(target?: Document | HTMLElement | null): void {
    setBodyShadowConvertedCss('')
    if (target != null) {
      const styleTagList = querySelectorAllComposed(target, '[data-nine-style=true]')
      for (let i = 0; i < styleTagList.length; i++) {
        styleTagList[i].parentElement?.removeChild(styleTagList[i])
      }
    } else {
      for (let i = 0; i < this._styleTagList.length; i++) {
        const info = this._styleTagList[i]
        info.darkModeTag.parentElement?.removeChild(info.darkModeTag)
      }
    }
  }

  async _imgToBase64(imgTag: HTMLImageElement): Promise<boolean> {
    const imgSrc =
      imgTag.src != null && imgTag.src.trim().length > 0 ? imgTag.src : imgTag.currentSrc
    if (imgSrc.indexOf('blob:') === 0) {
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', imgSrc, true)
        xhr.responseType = 'blob'
        // blob: URL 은 메모리 참조라 ms 단위 완료 — 5 초면 충분 (Phase 2 §8.3 #1).
        xhr.timeout = 5_000
        xhr.onload = function () {
          if (this.status === 200) {
            const myBlob = this.response as Blob
            const reader = new FileReader()
            reader.onloadend = () => {
              const data = reader.result as string
              if (data.indexOf('data:') === 0) {
                imgTag.setAttribute('src', data)
                resolve(true)
              } else {
                console.log(' unknown image data type. ', data)
                resolve(false)
              }
            }
            reader.onerror = () => {
              console.log(' FileReader onerror ')
              resolve(false)
            }
            reader.readAsDataURL(myBlob)
          } else {
            console.log(' blob xhr non-200 status: ', this.status)
            resolve(false)
          }
        }
        xhr.onerror = () => {
          console.log(' blob onerror ')
          resolve(false)
        }
        xhr.ontimeout = () => {
          console.log(' blob ontimeout ')
          resolve(false)
        }
        xhr.send()
      })
    }
    return Promise.resolve(false)
  }

  getLinkColor(): { link: string; visited: string } {
    return this._linkColor
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DocUtil — main document controller
// ─────────────────────────────────────────────────────────────────────────────

export class DocUtil {
  doc: Document
  nfCallback: ((param: string) => void) | null

  needRedefineCopy = true
  showRemoveZoomNodeBtn = true
  expandIconElem: HTMLElement | null = null

  // inner mode: host SPA 에 직접 마운트된 뷰어 루트(EmailContainer `.emailContainer` elem).
  // iframe/mobile: null → `get container` 가 doc.body 로 폴백(분리 문서 자체가 루트).
  // DocUtil 이 document 전역 대신 이 루트 기준으로 스코프를 잡기 위한 주입점(§15.14-⑥).
  rootContainer: HTMLElement | null = null

  mailViewMap: Record<string, MailView> = {}
  mailViewList: MailView[] = []

  colorMgr: EditorMgr
  parentType: string = 'desktop'

  private oldMetaTags: NodeListOf<HTMLMetaElement> | null = null
  private newMetaTag: HTMLElement | null = null

  private pre_css: HTMLElement | null = null

  private _nineDarkMode = false
  darkModeApplied = false

  // copy/cut 리스너가 현재 바인딩된 elem(inner=rootContainer/iframe=doc.body). null=미바인딩.
  // 등록/해제를 bindCopy/unbindCopy 로 일원화하고, 이 값으로 대칭 해제·중복 등록을 관리한다(§15.14-①).
  private copyBoundElem: HTMLElement | null = null
  private onCopy?: (e: ClipboardEvent) => Promise<void>
  private onCut?: (e: ClipboardEvent) => Promise<void>

  checkHeight?: (forced: boolean, isResize: boolean) => void

  get container(): HTMLElement {
    return this.rootContainer ?? this.doc.body
  }

  constructor(
    doc: Document,
    nfCallback: ((param: string) => void) | null,
    container?: HTMLElement | null,
  ) {
    this.doc = doc
    this.nfCallback = nfCallback
    this.rootContainer = container ?? null

    if (this.showRemoveZoomNodeBtn === true) {
      const expandIconData = `<svg height="32" id="expand" viewBox="0 0 32 32" width="32" xmlns="http://www.w3.org/2000/svg"><path d=" M16 4 L28 4 L28 16 L24 12 L20 16 L16 12 L20 8z M4 16 L8 20 L12 16 L16 20 L12 24 L16 28 L4 28z "/></svg>`
      this.expandIconElem = document.createElement('div')
      this.expandIconElem.className = 'nf-removeZoomBtn'
      setHTML(this.expandIconElem, expandIconData)
    }

    // win.singleUse 초기화는 helper/singleUse.ts (createSingleUseFunc) 에서 lazy 하게 수행.

    this.colorMgr = new EditorMgr(this)

    if (win._tmpDarkMode != null) {
      this.setDarkForeColor(win._tmpDarkMode.foreColor)
      this.setDarkBackColor(win._tmpDarkMode.backColor)
      this.setDarkMode(true)
    }
  }

  // rootContainer 주입/교체. DocUtil 생성이 EmailContainer 마운트보다 앞서거나(초기 emptyList),
  // 재마운트로 루트 elem 이 바뀔 때 호출된다(§15.14-⑥ empty→populate 재주입).
  // 컨테이너가 바뀌면 copy/cut 리스너도 새 rootContainer 로 재바인딩한다.
  setRootContainer(elem: HTMLElement | null): void {
    this.rootContainer = elem
    if (this.needRedefineCopy) this.bindCopy()
  }

  // 인라인 이미지 변환 캐시(EditorMgr.inlineDataUrlMap) 접근 API(§15.14-②).
  // EmailView.ensureInlineDataUrls(선변환)가 push, processCopy(copy)가 sync 조회.
  setInlineDataUrl(url: string, dataUrl: string): void {
    this.colorMgr.inlineDataUrlMap[url] = dataUrl
  }

  getInlineDataUrl(src: string): string | null {
    return this.colorMgr.inlineDataUrlMap[src] ?? null
  }

  // copy/cut 리스너를 현재 container(inner=rootContainer/iframe=doc.body)에 등록한다(§15.14-①).
  // 분산돼 있던 callOnReady·useOriginalCopy·setRootContainer 재주입을 이 한 경로로 통합.
  // copy 이벤트는 버블링되므로 rootContainer 자손(본문) 선택의 copy 만 도달한다.
  private bindCopy(): void {
    if (this.onCopy == null || this.onCut == null) return
    const target = this.container
    if (this.copyBoundElem === target) return
    this.unbindCopy()
    target.addEventListener('copy', this.onCopy)
    target.addEventListener('cut', this.onCut)
    this.copyBoundElem = target
  }

  private unbindCopy(): void {
    if (this.copyBoundElem != null && this.onCopy != null && this.onCut != null) {
      this.copyBoundElem.removeEventListener('copy', this.onCopy)
      this.copyBoundElem.removeEventListener('cut', this.onCut)
    }
    this.copyBoundElem = null
  }

  // resetMailView(재마운트/리셋) 시 호출 — rootContainer 리스너 해제 + zoom 타이머 정리(§15.14-⑧ cleanup).
  dispose(): void {
    this.unbindCopy()
    this.disposeZoomScheduler()
  }

  callWebview(param: string): void {
    if (this.nfCallback != null) this.nfCallback(param)
  }

  setViewMetaTag(value: boolean): void {
    if (value === true) {
      this.oldMetaTags = this.doc.head.querySelectorAll<HTMLMetaElement>('meta[name=viewport]')
      this.newMetaTag = createTag(
        '<meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes"/>',
      )
      if (this.oldMetaTags.length > 0) {
        this.doc.head.insertBefore(this.newMetaTag, this.oldMetaTags[0])
        for (let i = 0; i < this.oldMetaTags.length; i++) {
          this.doc.head.removeChild(this.oldMetaTags[i])
        }
      } else {
        this.doc.head.appendChild(this.newMetaTag)
      }
    } else if (value === false) {
      if (this.oldMetaTags != null && this.oldMetaTags.length > 0 && this.newMetaTag != null) {
        for (let i = 0; i < this.oldMetaTags.length; i++) {
          this.doc.head.insertBefore(this.oldMetaTags[i], this.newMetaTag)
        }
      }
      if (this.newMetaTag != null) this.doc.head.removeChild(this.newMetaTag)
    }
    printLog(
      'screen width - ',
      window.innerWidth,
      window.screen.availWidth,
      document.documentElement.offsetWidth,
    )
  }

  setPreCSS(): void {
    const style = `
<style id="pre-css">
  table {
    color: inherit;
  }
</style>
`
    this.pre_css = createTag(style)
    const firstStyle = this.doc.head.querySelector('style')
    if (firstStyle != null) {
      this.doc.head.insertBefore(this.pre_css, firstStyle)
    } else {
      this.doc.head.appendChild(this.pre_css)
    }
  }

  addViewElement(key: string, element: HTMLElement): void {
    let mvObj: MailView
    if (this.mailViewMap[key] == null) {
      mvObj = new MailView(document, key, element, callWebView, this)
      this.mailViewMap[key] = mvObj
      this.mailViewList.push(mvObj)
    } else {
      mvObj = this.mailViewMap[key]
      if (mvObj.container === element) return
      mvObj.setContainer(element)
    }

    if (mvObj.container.style.visibility === 'hidden') {
      win._bodyBgColor = mvObj.container.getAttribute('bgcolor')
      mvObj.container.removeAttribute('bgcolor')
    }

    mvObj.onAdded()
  }

  removeViewElement(key: string): void {
    const mvObj = this.mailViewMap[key]
    if (mvObj != null) {
      mvObj.dispose()

      const idx = this.mailViewList.indexOf(mvObj)
      if (idx > -1) this.mailViewList.splice(idx, 1)
      delete this.mailViewMap[key]
    }
  }

  // 메일박스/스레드 전환 시 mailViewList 누적을 방지하기 위해 전체 일괄 정리.
  // dispose는 멱등(container == null이면 즉시 return)하므로 중복 호출에 안전.
  clearAllViewElements(): void {
    const list = this.mailViewList
    for (let i = 0; i < list.length; i++) {
      try {
        list[i].dispose()
      } catch (e) {
        console.warn('[DocUtil] MailView dispose 실패:', e)
      }
    }
    this.mailViewList = []
    this.mailViewMap = {}
  }

  notifyResized(): void {
    this.mailViewList.forEach((mvObj) => mvObj.notifyResized())
  }

  // ── 통합 zoom 재계산 스케줄러 (R7) ─────────────────────────────────────────
  // 메일뷰별 개별 setTimeout 대신 dirty set + 단일 타이머로 재계산 요청을 모아서 처리.
  // ZOOM_DEBOUNCE: 요청 후 대기, ZOOM_MIN_INTERVAL: 직전 실행과의 최소 간격(스로틀).
  private static readonly ZOOM_DEBOUNCE = 300
  private static readonly ZOOM_MIN_INTERVAL = 500
  private zoomDirtySet = new Set<MailView>()
  private zoomTimer: ReturnType<typeof setTimeout> | null = null
  // 높이 통지 전용 — 대기 중인 zoom 요청 타이머를 취소하지 않도록 분리.
  private zoomHeightTimer: ReturnType<typeof setTimeout> | null = null
  private zoomLastRunTime = 0

  requestZoom(mvObj: MailView): void {
    this.zoomDirtySet.add(mvObj)
    if (this.zoomTimer != null) clearTimeout(this.zoomTimer)
    const elapsed = Date.now() - this.zoomLastRunTime
    const delay = Math.max(DocUtil.ZOOM_DEBOUNCE, DocUtil.ZOOM_MIN_INTERVAL - elapsed)
    this.zoomTimer = setTimeout(() => this.runZoomScheduler(), delay)
  }

  private runZoomScheduler(): void {
    this.zoomTimer = null
    this.zoomLastRunTime = Date.now()
    const targets = Array.from(this.zoomDirtySet)
    this.zoomDirtySet.clear()
    let processed = 0
    targets.forEach((mvObj) => {
      // 접힌(높이 0)·해제된 메일뷰는 skip — 펼칠 때 notifyVisible 경로로 처리됨. (R3)
      if (mvObj.isZoomDisposed() === true || mvObj.container.offsetHeight === 0) return
      mvObj.runPartialZoom()
      processed += 1
    })
    if (processed > 0) this.notifyZoomHeight() // 실제 처리된 대상이 있을 때만 높이 통지.
  }

  // 높이만 변한 경우 — 재계산 없이 네이티브 높이 통지만 수행. (R6)
  notifyZoomHeightOnly(): void {
    if (this.zoomHeightTimer != null) clearTimeout(this.zoomHeightTimer)
    this.zoomHeightTimer = setTimeout(() => {
      this.zoomHeightTimer = null
      this.notifyZoomHeight()
    }, DocUtil.ZOOM_DEBOUNCE)
  }

  private notifyZoomHeight(): void {
    this.colorMgr.nativeCall('resizeHeight', null, [document.documentElement.scrollHeight])
  }

  cancelZoom(mvObj: MailView): void {
    this.zoomDirtySet.delete(mvObj)
  }

  // resetMailView → initMailView 재진입 시 이전 DocUtil의 pending 타이머가
  // stale 상태로 발화하지 않도록 정리. (orphan 타이머 방지)
  disposeZoomScheduler(): void {
    if (this.zoomTimer != null) {
      clearTimeout(this.zoomTimer)
      this.zoomTimer = null
    }
    if (this.zoomHeightTimer != null) {
      clearTimeout(this.zoomHeightTimer)
      this.zoomHeightTimer = null
    }
    this.zoomDirtySet.clear()
  }

  setBodyVisible(val: boolean): void {
    // 분리 문서(iframe/mobile)의 렌더 완료 후 body 를 노출하는 native 연동. inner 에서는 host body 를
    // 건드리면 안 되고(앱 전체 영향), 뷰어 가시성은 EmailContainer(React loading 상태)가 관리하므로 no-op.
    if (!isIframe) return
    if (val === true) {
      if (win._bodyBgColor != null && this.isDarkMode() !== true) {
        document.body.setAttribute('bgcolor', win._bodyBgColor)
      }
      this.doc.body.style.visibility = ''
    } else {
      this.doc.body.style.visibility = 'hidden'
    }
  }

  checkEditableElem(): void {
    // 이메일 본문을 편집 불가로 만든다. 전역 document 스캔은 inner 에서 host 의 contenteditable
    // (작성 에디터 등)까지 해제하는 오염이라, 뷰어 루트(container=rootContainer/iframe=doc.body)로 한정한다.
    const editableElems = this.container.querySelectorAll<HTMLElement>('[contenteditable]')
    for (let i = 0; i < editableElems.length; i++) {
      if (editableElems[i].getAttribute('contenteditable') === 'true') {
        editableElems[i].removeAttribute('contenteditable')
      }
    }
  }

  private onReadyMailView(): void {
    if (window.isReactReady === true && window.isMailViewReady === true) {
      this.colorMgr.nativeCall('onReady', (ret) => {
        console.log(' -- docUtil - onReady - ret : ', ret)
      })
    }
  }

  callOnReady(isDarkMode: boolean = false): void {
    this.parentType = 'desktop'
    if (window.parent != null && window.parent !== window) {
      const parentInitNative = (window.parent as ParentLikeWindow).initNative
      if (parentInitNative != null && resolveNineNative() == null) {
        const ret = parentInitNative()
        if (typeof ret === 'string') this.parentType = ret
      }
    }
    const nine = resolveNineNative()
    if (nine?.getNativeType != null) {
      this.parentType = nine.getNativeType()
    }

    setTimeout(() => {
      console.log(' -- docUtil - onReady : ')
      window.isMailViewReady = true
      this.onReadyMailView()
    }, 10)

    const processCopy = async (e: ClipboardEvent, _isCut: boolean): Promise<void> => {
      const target = e.target as HTMLElement | null
      if (target?.tagName.toLowerCase() === 'input') return

      const selection = document.getSelection()
      if (selection == null || e.clipboardData == null) return

      // §15.15-3: cross-boundary 선택(본문↔host UI)의 host UI 혼입 방어 — anchor·focus 둘 다
      // container(inner=rootContainer / iframe=doc.body) 내부일 때만 처리. 걸친 선택은 기본 복사 위임.
      // bodyIsolation 'shadow-dom' 면 composed range 로 판정한다(resolveBodyCopyRange).
      const rng = resolveBodyCopyRange(selection, [this.container])
      if (rng == null) return

      e.clipboardData.setData('text/plain', selection.toString())

      if (selection.rangeCount) {
        const cloneFragment = rng.cloneContents()
        const matched = cloneFragment.querySelector('.mail-message-content') as HTMLElement | null
        const realBody: HTMLElement | DocumentFragment = matched ?? cloneFragment

        const targetElem = document.createElement('body')
        while (realBody.firstChild != null) {
          targetElem.appendChild(realBody.firstChild)
        }

        if (this.mailViewList.length > 0) {
          this.mailViewList[0].removePartialZoom(targetElem, false, true)
        }

        // copy-color-passthrough: 선택 영역 복사 시 "조상 글자색 강제 주입" 을 비활성화한다.
        // 브라우저 기본 복사가 전달하는 노드 구조를 최대한 그대로 두는 것이 원칙 — 임의 가공이
        // 늘수록 예외 상황이 늘고 전수 대응이 불가능해진다.
        //
        // [비활성화한 동작] 아래 블록은 rng.commonAncestorContainer 에서 위로 올라가며 "선택 영역
        // 밖" 의 가장 가까운 color 보유 조상을 찾아, 그 색(style.color)과 data-nine-sc 를 복사본
        // 최상위 자식(targetElem.children)에 주입했다. cloneContents() 가 선택 밖 조상을 버려서
        // 조상에서 "상속" 받던 글자색이 clipboard 에서 사라지는 것을 보완하려는 의도였다.
        //
        // [비활성화 이유]
        //  1) 직계 자식(targetElem.children)에만 주입 → <ul> 등 컨테이너의 자손(li>p)은 색을 받지
        //     못해, 같은 선택인데 최상위는 색이 있고 중첩 요소는 없어지는 "불일치" 를 clipboard
        //     단계에서 만들어냈다.
        //  2) 원래 color 가 없던 요소에 색을 합성해 박는 방식이라, 붙여넣는 편집기 쪽 규칙
        //     (ProseMirror 리스트 노드의 블록 color 미보존 + stripDefaultColors 의 "기본색과
        //     같으면 제거") 과 맞물려, 신규/회신 컨텍스트마다 색이 제각각 나타나는 근본 원인이
        //     되었다.
        //
        // [감수하는 트레이드오프] 글자색이 "선택 영역 밖 조상" 에서만 상속되던 경우, 그 색은
        // 복사본에 포함되지 않아 외부 앱(Gmail·Word 등)·기본색이 다른 편집기에 붙이면 기본색으로
        // 보일 수 있다. 단, 요소 자신/선택 안 조상/span 에 "명시" 된 색은 clone 에 그대로 포함되어
        // 영향이 없고, 본문이 기본 검정색인 일반 메일도 영향이 없다(비기본색이 컨테이너에만 걸린
        // 경우에 한정). 다크모드 원색 복원(_restoreElemColor, data-nine-sc)도 선택 안에 포함된
        // 요소에는 그대로 동작한다.
        //
        // let tmpNode: Node | null = rng.commonAncestorContainer
        // while (
        //   tmpNode != null &&
        //   tmpNode.nodeType === 1 &&
        //   (tmpNode as HTMLElement).style.color.length === 0
        // ) {
        //   tmpNode = tmpNode.parentNode
        // }
        //
        // if (tmpNode != null && tmpNode.nodeType === 1) {
        //   const tmpEl = tmpNode as HTMLElement
        //   const nsc = tmpEl.getAttribute('data-nine-sc')
        //   const sc = tmpEl.style.color
        //   const children = targetElem.children
        //   for (let i = 0; i < children.length; i++) {
        //     const child = children[i] as HTMLElement
        //     if (child.style.color.length === 0) {
        //       child.style.color = sc
        //       if (nsc != null) child.setAttribute('data-nine-sc', nsc)
        //     }
        //   }
        // }

        const imgs = targetElem.querySelectorAll('img')
        // 뷰어 내부 전용 attr 제거(processCopy 고유). owa-*/data-cid 는 serializeInlineImages 가 담당.
        imgs.forEach((img) => {
          img.setAttribute('src', img.src)
          ;[
            'data-mce-id',
            'data-mce-object',
            'data-mce-src',
            'data-nine-resize',
            'data-nine-anichk',
            'data-nine-mimetype',
            'data-nine-dpi',
            'data-nine-src',
            'data-nine-size',
            'data-nine-flip-h',
            'data-nine-flip-v',
            'data-nine-rotate',
          ].forEach((attr) => img.removeAttribute(attr))
        })

        // 인라인 이미지 sync 치환: 선변환 map(EditorMgr) 조회 → data: 교체 + owa-src(차단 외부이미지)
        // 복원 + 인라인 마커 제거(§15.14-②). await 없이 동기 수행해 WKWebView clipboard 계약을 지킨다.
        serializeInlineImages(targetElem, this.colorMgr.inlineDataUrlMap, { upgradeInsecureUrl })

        // map miss(선변환 미완료)로 blob: 이 남은 img 만 _imgToBase64 로 best-effort 폴백(§15.14-⑧).
        // 예열이 대부분 채우므로 드물고, 폴백 await 는 WKWebView 에서 계약이 깨질 수 있어 방어적 용도다.
        for (let i = 0; i < imgs.length; i++) {
          const src = imgs[i].getAttribute('src')
          if (src != null && src.indexOf('blob:') === 0) {
            await this.colorMgr._imgToBase64(imgs[i])
          }
        }

        const aTags = targetElem.querySelectorAll('a')
        for (let i = 0; i < aTags.length; i++) {
          const href = aTags[i].href
          aTags[i].setAttribute('href', href)
          aTags[i].setAttribute('data-mce-href', href)
        }

        if (this.isDarkMode() === true) {
          this.colorMgr._restoreElemColor(targetElem)
        }

        e.clipboardData.setData('text/html', targetElem.innerHTML)
      }

      e.preventDefault()
    }

    // 재정의 전 기존 리스너 해제(재진입 안전).
    this.unbindCopy()

    this.onCopy = async (e: ClipboardEvent): Promise<void> => {
      await processCopy(e, false)
    }
    this.onCut = async (e: ClipboardEvent): Promise<void> => {
      await processCopy(e, true)
    }

    // §15.14-①: inner/iframe 공통으로 container(inner=rootContainer / iframe=doc.body 폴백)에 등록.
    // copy 이벤트 버블링으로 본문(container 자손) 선택의 copy 만 도달하고, host UI 선택은 리스너에
    // 닿지 않아 기본 복사에 위임된다(구 isIframe 게이트 제거). rootContainer 미주입(emptyList)이면
    // 이후 setRootContainer 가 재바인딩한다.
    if (this.needRedefineCopy) this.bindCopy()

    if (isDarkMode === true) {
      this.setDarkMode(true)
    }
  }

  useOriginalCopy(value: boolean): void {
    this.needRedefineCopy = value === false
    if (this.needRedefineCopy) this.bindCopy()
    else this.unbindCopy()
  }

  setIOSVersion(version: string | number): void {
    const verStrs = String(version).split(/[._]/)
    const majorVersion = window.parseInt(verStrs[0], 10)
    console.log(' -- major version : ', majorVersion)
    if (majorVersion > 16) this.useOriginalCopy(true)
  }

  isDarkMode(): boolean {
    return this._nineDarkMode
  }

  // 다크모드 색상 워크는 뷰어 루트(this.container = inner:rootContainer / iframe:doc.body)에 한 번만
  // 적용한다 — _checkElemColor/_checkStyleTagCss/_restore* 가 재귀적이라 하위 본문 전체로 전파된다.
  // 루트로 스코프를 잡으므로 host chrome(subject·툴바)은 오염되지 않는다.
  setDarkMode(isDarkMode: boolean): void {
    if (this._nineDarkMode === isDarkMode) return
    this._nineDarkMode = isDarkMode

    // 루트 표면(inner=rootContainer / iframe=doc.body 폴백). 다크 배경이 비치도록 투명화한다.
    const docElem = this.container
    let savedValue: string | null = null

    if (isDarkMode === true) {
      if (docElem != null && docElem.style.backgroundColor.length > 0) {
        savedValue = docElem.getAttribute('data-nine-ac')
        if (savedValue == null) {
          docElem.setAttribute('data-nine-ac', docElem.style.backgroundColor)
          docElem.style.backgroundColor = 'transparent'
        }
      }
      this.darkModeApplied = true
      this.colorMgr.darkModeInfo.backLum = chroma(this.colorMgr.darkModeInfo.backColor).luminance()
      this.colorMgr.darkModeInfo.foreLum = chroma(this.colorMgr.darkModeInfo.foreColor).luminance()

      this.colorMgr._checkElemColor(this.container)
      this.setDarkBackColor(this.colorMgr.darkModeInfo.backColor)
      this.setDarkForeColor(this.colorMgr.darkModeInfo.foreColor)

      this.colorMgr._checkStyleTagCss(this.doc)
      this.container.style.color = this.colorMgr.darkModeInfo.foreColor
      this.container.style.backgroundColor = this.colorMgr.darkModeInfo.backColor
    } else {
      if (docElem != null) {
        savedValue = docElem.getAttribute('data-nine-ac')
        if (savedValue != null) {
          docElem.style.backgroundColor = savedValue
          docElem.removeAttribute('data-nine-ac')
        }
      }
      this.colorMgr._restoreElemColor(this.container)
      this.colorMgr._restoreStyleTagCss(this.doc)
      this.container.style.color = ''
      this.container.style.backgroundColor = ''
      this.removeDarkForeColor()
    }

    // 네이티브 UI(폼 컨트롤·스크롤바 등)를 테마에 맞추기 위해 html(this.doc.documentElement)에
    // 인라인 style 로 color-scheme 을 지정한다: 다크면 'dark', 아니면 제거.
    const htmlElem = this.doc.documentElement
    if (isDarkMode === true) {
      htmlElem.style.setProperty('color-scheme', 'dark')
      htmlElem.setAttribute('data-kit-theme', 'dark')
    } else {
      htmlElem.style.removeProperty('color-scheme')
      htmlElem.setAttribute('data-kit-theme', 'default')
    }
  }

  applyDarkMode(): void {
    if (this.darkModeApplied === true && this._nineDarkMode === true) {
      this.colorMgr._restoreElemColor(this.container)
      this.colorMgr._restoreStyleTagCss(this.doc)
      this.container.style.color = ''
      this.container.style.backgroundColor = ''
      this.removeDarkForeColor()

      this.colorMgr._checkElemColor(this.container)
      // 위에서 지운 #darkmode-info(본문 다크 링크·글자색)와 그 shadow 미러를 setDarkMode(true) 와 같은 순서로
      // 다시 넣는다 — 빠지면 본문 mount 마다 다크 링크 규칙이 사라졌다.
      this.setDarkForeColor(this.colorMgr.darkModeInfo.foreColor)
      this.colorMgr._checkStyleTagCss(this.doc)
      this.container.style.color = this.colorMgr.darkModeInfo.foreColor
      this.container.style.backgroundColor = this.colorMgr.darkModeInfo.backColor
    }
  }

  getDarkBackColor(): string {
    return this.colorMgr.darkModeInfo.backColor
  }

  // 캐시된 다크 변환 태그 참조(<style>/<link>.darkModeElem)를 모두 비운다 — 팔레트(back/fore) 변경
  // 후 다음 _checkStyleTagCss 가 옛 색이 아닌 새 색으로 재파싱하도록 강제한다.
  clearCachedStyle(): void {
    querySelectorAllComposed<HTMLElement>(this.doc, 'style, link[rel="stylesheet"]').forEach(
      (el) => {
        delete el.darkModeElem
      },
    )
  }

  setDarkBackColor(color: string): void {
    const newCol = color.toLowerCase()
    if (this.colorMgr.darkModeInfo.backColor !== newCol) {
      this.colorMgr.darkModeInfo.backColor = newCol
      this.clearCachedStyle()
    }
    if (this.isDarkMode() === true) {
      this.container.style.backgroundColor = this.colorMgr.darkModeInfo.backColor
    }
  }

  getDarkForeColor(): string {
    return this.colorMgr.darkModeInfo.foreColor
  }

  setDarkForeColor(color: string): void {
    const newCol = color.toLowerCase()
    if (this.colorMgr.darkModeInfo.foreColor !== newCol) {
      this.colorMgr.darkModeInfo.foreColor = newCol
      this.clearCachedStyle()
    }
    if (this.isDarkMode() === true) {
      const doc = document
      let styleTag = doc.querySelector<HTMLStyleElement>('#darkmode-info')
      if (styleTag == null) {
        styleTag = doc.createElement('style')
        styleTag.id = 'darkmode-info'
      }
      // §15.14-⑨: 전역 `body`/`a:link` 셀렉터는 inner(host SPA)에서 host 전역을 오염시킨다.
      // 본문 스코핑 클래스 `.htmlMailBody` 프리픽스로 재작성해 뷰어 본문에만 적용한다(iframe 에도
      // 본문은 항상 `.htmlMailBody` 로 래핑되므로 동일하게 동작 — @scope 불필요).
      let cssDec = '\n  .htmlMailBody { color: ' + this.colorMgr.darkModeInfo.foreColor + '; }'
      const linkSrc = this.colorMgr.getLinkColor().link
      const visitedSrc = this.colorMgr.getLinkColor().visited
      const linkColor = this.colorMgr._getDarkModeColor(null, linkSrc, false) ?? linkSrc
      const visitedColor = this.colorMgr._getDarkModeColor(null, visitedSrc, false) ?? visitedSrc
      cssDec += '\n  .htmlMailBody a:link { color: ' + linkColor + '; }'
      cssDec += '\n  .htmlMailBody a:visited { color: ' + visitedColor + '; }'
      cssDec += '\n'
      setHTML(styleTag, cssDec)
      doc.head.appendChild(styleTag)
      // shadow 본문에는 document 의 #darkmode-info 가 닿지 않으므로 같은 선언을 shadow sheet 에 미러한다.
      setBodyShadowDarkCss(cssDec)
    }
  }

  private removeDarkForeColor(): void {
    const styleTag = this.doc.querySelector('#darkmode-info')
    if (styleTag != null) styleTag.parentElement?.removeChild(styleTag)
    setBodyShadowDarkCss('')
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MailView — per-element view manager (zoom / smartview / resize)
// ─────────────────────────────────────────────────────────────────────────────

// 부분 zoom 대상이 확실한 최상위 자식(inline style 의 px width/min-width 가 본문 폭 초과)을 **첫 layout 전**
// 분리된 fragment 에서 미리 `nf-zoom-container > nf-zoom-wrapper` 로 감싼다. 그러면 applyZoom 은 기존
// container 분기에서 transform·크기만 정하고, 이미 layout 된 대형 subtree 를 옮기지 않는다(옮기면 subtree
// 전체 style·layout 을 다시 한다 — 요소 22만 본문에서 약 5초). 실제 폭·높이·비율은 applyZoom 이 layout 후
// 잰다. 감쌌는데 zoom 이 필요 없으면 applyZoom 이 removePartialZoom 으로 되돌린다(기존 비용, 드문 경우).
// ponytail: 최상위 자식만 본다 — 중첩 overflow(div > table[width])는 기존 이동 경로로 처리된다.
const PREWRAP_INLINE_TAGS = new Set([
  'SPAN',
  'A',
  'B',
  'I',
  'U',
  'EM',
  'STRONG',
  'FONT',
  'IMG',
  'BR',
  'LABEL',
  'SMALL',
  'SUB',
  'SUP',
  'CODE',
])
// expandIcon: 펼치기 버튼 원본(DocUtil.expandIconElem). 있으면 position:relative 와 버튼 복제본까지 layout
// 전에 넣어 둔다 — layout 후에 버튼을 붙이거나 position 을 바꾸면 subtree 전체를 다시 layout 한다.
// click handler 는 applyZoom 이 zoom 을 실제로 적용할 때 연결한다.
export function prewrapOverflowChildren(
  root: ParentNode,
  bodyWidth: number,
  expandIcon: HTMLElement | null,
): number {
  if (!(bodyWidth > 0)) return 0
  const pxOf = (p: CSSPropInfo): number => (p.type === 1 && p.isRel === false ? (p.num ?? 0) : 0)
  let n = 0
  for (const child of Array.from(root.children) as HTMLElement[]) {
    if (child.classList.contains('nf-zoom-container') || PREWRAP_INLINE_TAGS.has(child.tagName))
      continue
    if (child.getAttribute('data-nine-zoom-disabled') === 'true') continue
    // bake 가 stylesheet 를 inline style 로 옮겨 두므로 inline 값만 보면 된다. 흐름 밖·inline·비표시는
    // zoom 대상이 아니고, absolute 는 container 의 position:relative 가 기준 상자를 바꾼다.
    const { display, position } = child.style
    if (
      display === 'inline' ||
      display === 'none' ||
      position === 'absolute' ||
      position === 'fixed'
    )
      continue
    // max-width 가 %·vw 면 본문에 맞춰지고, px 면 width 를 그 값으로 자른다(min-width 가 우선).
    const maxW = getCSSProp(child.style.maxWidth)
    if (maxW.type === 1 && maxW.isRel !== false) continue
    const cap = maxW.type === 1 ? (maxW.num ?? Infinity) : Infinity
    const px = Math.max(
      Math.min(pxOf(getCSSProp(child.style.width)), cap),
      pxOf(getCSSProp(child.style.minWidth)),
    )
    if (px <= bodyWidth) continue
    const container = createTag('<div class="nf-zoom-container"></div>')
    const wrapper = createTag('<div class="nf-zoom-wrapper"></div>')
    // 실제 값은 applyZoom 의 container 분기가 layout 후 다시 읽어 채운다.
    const zoomInfo: ZoomInfo = { originWidth: 0, originHeight: 0 }
    container.zoomInfo = zoomInfo
    child.zoomInfo = zoomInfo
    child.setAttribute('data-nine-zoom-elem', 'true')
    container.appendChild(wrapper)
    if (expandIcon != null) {
      container.style.position = 'relative'
      container.appendChild(expandIcon.cloneNode(true))
    }
    root.insertBefore(container, child)
    wrapper.appendChild(child)
    n++
  }
  return n
}

export class MailView {
  state = -1
  partialZoomMode = false
  smartViewMode = false
  zoomApplied = false

  options: MailViewOptions = {}
  nfCallback: ((param: string) => void) | null

  doc: Document
  docUtil: DocUtil
  key: string
  container: HTMLElement

  observerObj: ResizeObserverEntry | null = null
  isMediaQuery: boolean
  isHScroll = false
  optimizeEnabled = false
  isOnReadyCalled = false

  private originBodyInfo: BodyOriginInfo | null = null
  private bodyWidth = 0
  minZoom = 1
  private applyZoomTimer: number | null = null
  // OMD-239: resize 전이 직후 거짓 래핑을 레이아웃 안정화 후 1회 재평가(치유)하기 위한 rAF 핸들.
  private zoomSettleRaf: number | null = null
  private disposed = false

  // 서킷 브레이커(R4) 상태 — runPartialZoom에서 사용.
  private zoomRunLog: number[] = []
  private zoomSuspendedUntil: number | null = null
  private lastZoomDelta: number | null = null
  private zoomResumeTimer: ReturnType<typeof setTimeout> | null = null

  constructor(
    doc: Document,
    key: string,
    container: HTMLElement | null,
    nfCallback: ((param: string) => void) | null,
    docUtil: DocUtil,
  ) {
    this.doc = doc
    this.docUtil = docUtil
    this.nfCallback = nfCallback
    this.key = key
    this.container = container || doc.body

    this.container.setAttribute('data-mailview', 'true')
    this.container.mvObj = this

    this.isMediaQuery = this.checkMediaQuery()
    this.attachResizeEvent()
  }

  get colorMgr(): EditorMgr {
    return this.docUtil.colorMgr
  }

  checkMediaQuery(): boolean {
    // 이 메일 본문의 `<style>` 에 media query 가 있는지 감지. inner 에서 전역 document 를 스캔하면
    // host 앱 CSS 의 media query 까지 잡혀 항상 true 가 되므로, 본문(container)으로 한정한다(§15.13).
    // iframe 은 분리 문서라 기존대로 doc 전체를 본다(본문 style 이 head 에 있을 수 있음).
    const scope = isIframe ? this.doc : this.container
    const styleElems = scope.querySelectorAll<HTMLStyleElement>('style')
    for (let i = 0; i < styleElems.length; i++) {
      const sheet = styleElems[i].sheet
      if (sheet == null) continue
      for (let j = 0; j < sheet.cssRules.length; j++) {
        if (sheet.cssRules[j] instanceof CSSMediaRule) return true
      }
    }
    return false
  }

  hasMediaQuery(): boolean {
    return this.isMediaQuery
  }

  checkHScroll(): boolean {
    // Safari scrollWidth +1px 아티팩트로 순수 텍스트 메일에서 partialZoom 이 오활성되지 않도록
    // 가용 폭(offsetWidth) 쪽에 버퍼를 둔다. (OMD-239)
    return this.container.offsetWidth + SAFARI_SCROLLW_BUFFER < this.container.scrollWidth
  }

  hasHScroll(): boolean {
    this.isHScroll = this.checkHScroll()
    if (!this.optimizeEnabled && this.isHScroll) {
      this.optimizeEnabled = true
      if (this.isOnReadyCalled === true) {
        console.log(' --- onOptimizeEnable')
        this.colorMgr.nativeCall('onOptimizeEnable', null, [])
      }
    }
    return this.isHScroll
  }

  setOptions(options: MailViewOptions): void {
    this.options = options
    if (this.options.mode === 'normal') this.setOptimized(false)
    else if (this.options.mode === 'optimized') this.setOptimized(true)
  }

  setBodyVisible(val: boolean): void {
    this.container.style.visibility = val === true ? '' : 'hidden'
  }

  showOptimizeBtn(val: boolean): void {
    if (win.webObj?.setOptimizedBtn != null) {
      win.webObj.setOptimizedBtn(this.key, val)
    }
  }

  hideAllImgs(val: boolean): void {
    const imgTags = this.container.querySelectorAll<HTMLImageElement>('img')
    for (let i = 0; i < imgTags.length; i++) {
      if (imgTags[i].initVisibility == null) {
        imgTags[i].initVisibility = imgTags[i].style.visibility
      }
      if (imgTags[i].initVisibility !== 'hidden') {
        if (val === true) {
          imgTags[i].style.visibility = 'hidden'
        } else {
          imgTags[i].style.visibility = imgTags[i].initVisibility || ''
        }
      }
    }
  }

  removePartialZoom(
    elem?: HTMLElement | null,
    removeImgEvent?: boolean,
    isCopy?: boolean,
    forced?: boolean,
  ): HTMLElement | null {
    const container: HTMLElement = elem || this.container

    if (elem == null) {
      const bInfo = this.originBodyInfo
      if (bInfo != null) {
        if (bInfo.width.length > 0) {
          bInfo.elem.style.setProperty('width', bInfo.width, bInfo.widthPriority)
        } else {
          bInfo.elem.style.removeProperty('width')
        }
        if (bInfo.minWidth.length > 0) {
          bInfo.elem.style.setProperty('min-width', bInfo.minWidth, bInfo.minWidthPriority)
        } else {
          bInfo.elem.style.removeProperty('min-width')
        }
      }
    }

    const removeZoomAttr = (node: HTMLElement): void => {
      const attr = node.getAttribute('data-nine-zoom-style')
      if (attr != null && attr.length > 0) {
        node.setAttribute('style', attr)
      } else {
        node.removeAttribute('style')
      }
      node.removeAttribute('data-nine-zoom-style')
      node.removeAttribute('data-nine-zoom-elem')
    }

    if (
      (container as Element) !== this.doc.documentElement &&
      container.getAttribute('data-nine-zoom-style') != null
    ) {
      removeZoomAttr(container)
    } else {
      const attrElems = container.querySelectorAll<HTMLElement>('[data-nine-zoom-style]')
      for (let i = 0; i < attrElems.length; i++) removeZoomAttr(attrElems[i])
    }

    let ret: HTMLElement | null = null

    const removeWrapper = (node: HTMLElement): void => {
      // 제거되는 컨테이너 하위 확대버튼의 click 리스너 해제 (미클릭 잔존 방지). (R20)
      const expandBtns = node.querySelectorAll<ExpandBtnElement>('.nf-removeZoomBtn')
      for (let b = 0; b < expandBtns.length; b++) {
        const handler = expandBtns[b].__nineExpandHandler
        if (handler != null) {
          expandBtns[b].removeEventListener('click', handler)
          expandBtns[b].__nineExpandHandler = undefined
        }
      }
      while (node.firstElementChild != null && node.firstElementChild.children.length > 0) {
        ret = node.firstElementChild.firstElementChild as HTMLElement
        node.parentElement?.insertBefore(ret, node)
      }
      node.parentElement?.removeChild(node)

      if (elem != null && forced === true && ret != null) {
        ret.setAttribute('data-nine-zoom-disabled', 'true')
      }
    }

    if (
      (container as Element) !== this.doc.documentElement &&
      container.classList.contains('nf-zoom-container') === true
    ) {
      removeWrapper(container)
    } else if (
      (container as Element) !== this.doc.documentElement &&
      container.getAttribute('data-nine-zoom-elem') != null
    ) {
      if (
        container.parentElement != null &&
        container.parentElement.classList.contains('nf-zoom-wrapper') === true
      ) {
        if (container.parentElement.parentElement != null) {
          removeWrapper(container.parentElement.parentElement)
        }
      } else {
        ret = container
      }
    } else {
      const elems = container.querySelectorAll<HTMLElement>('.nf-zoom-container')
      for (let i = 0; i < elems.length; i++) removeWrapper(elems[i])
      const wrapperElems = container.querySelectorAll<HTMLElement>('.nf-zoom-wrapper')
      for (let i = 0; i < wrapperElems.length; i++) {
        const node = wrapperElems[i]
        while (node.children.length > 0) {
          ret = node.firstElementChild as HTMLElement
          node.parentElement?.insertBefore(ret, node)
        }
        node.parentElement?.removeChild(node)
      }
    }

    if ((container as Element) === this.doc.documentElement || isCopy === true) {
      const tmpElems = container.querySelectorAll<HTMLElement>('[data-nine-zoom-elem]')
      for (let i = 0; i < tmpElems.length; i++) tmpElems[i].removeAttribute('data-nine-zoom-elem')
    }

    if (container != null) {
      const tmpElems = container.querySelectorAll<HTMLElement>('[data-nine-zoom-disabled]')
      for (let i = 0; i < tmpElems.length; i++)
        tmpElems[i].removeAttribute('data-nine-zoom-disabled')
    }

    if (removeImgEvent !== false) {
      const imgTags = this.container.querySelectorAll<HTMLImageElement>('img')
      for (let i = 0; i < imgTags.length; i++) {
        if (imgTags[i].imgListener != null) {
          imgTags[i].removeEventListener('load', imgTags[i].imgListener as EventListener)
          imgTags[i].removeEventListener('error', imgTags[i].imgListener as EventListener)
        }
      }
    }

    this.zoomApplied = false
    return ret
  }

  // 부분 zoom container 에 펼치기(원본 크기) 버튼을 붙인다. 클릭하면 이 container 의 zoom 을 해제한다.
  private attachExpandBtn(container: HTMLElement): void {
    if (this.docUtil.expandIconElem == null) return
    container.style.position = 'relative'
    const expandBtnNode = this.docUtil.expandIconElem.cloneNode(true) as ExpandBtnElement
    container.appendChild(expandBtnNode)
    this.bindExpandBtn(container, expandBtnNode)
  }

  private bindExpandBtn(container: HTMLElement, expandBtnNode: ExpandBtnElement): void {
    const onClickExpand = (e: MouseEvent): void => {
      this.removePartialZoom(container, false, false, true)
      e.stopPropagation()
      e.preventDefault()
      expandBtnNode.removeEventListener('click', onClickExpand)
      expandBtnNode.__nineExpandHandler = undefined
    }
    // 미클릭 상태로 dispose/removeWrapper될 때 리스너를 해제할 수 있도록 핸들러 보관.
    expandBtnNode.__nineExpandHandler = onClickExpand
    expandBtnNode.addEventListener('click', onClickExpand)
  }

  private applyPartialZoom(isSettlePass = false): void {
    const bodyElem = this.container

    const marginLeft = getCSSProp(window.getComputedStyle(bodyElem, null).marginLeft)
    const marginRight = getCSSProp(window.getComputedStyle(bodyElem, null).marginRight)

    let lMargin = 0
    let rMargin = 0
    if (marginLeft.type === 1) lMargin += marginLeft.number ?? 0
    if (marginRight.type === 1) rMargin += marginRight.number ?? 0

    // 재호출 시 직전 호출이 설정한 width(calc(100% - …))가 원본으로 저장되지 않도록 최초 1회만 저장.
    if (this.originBodyInfo == null) {
      this.originBodyInfo = {
        elem: bodyElem,
        width: bodyElem.style.width,
        widthPriority: bodyElem.style.getPropertyPriority('width'),
        minWidth: bodyElem.style.minWidth,
        minWidthPriority: bodyElem.style.getPropertyPriority('min-width'),
      }
    }

    bodyElem.style.setProperty('width', 'calc(100% - ' + (lMargin + rMargin) + 'px)', 'important')
    bodyElem.style.setProperty('min-width', 'unset', 'important')

    const bodyRect = bodyElem.getBoundingClientRect()
    this.bodyWidth = Math.round(bodyRect.width)

    // 부분 zoom container 를 풀어 원래 요소를 돌려준다. 같은 pass 에서 이미 zoom 한 다른 container 가 있을 수
    // 있으므로 zoomApplied 는 유지한다(removePartialZoom 은 무조건 false 로 만든다).
    const unwrapZoom = (container: HTMLElement): HTMLElement | null => {
      const applied = this.zoomApplied
      const elem = this.removePartialZoom(container, false, false)
      this.zoomApplied = applied
      elem?.removeAttribute('data-nine-zoom-elem')
      return elem
    }

    const applyZoom = (targetElem: HTMLElement): void => {
      // L1 — DOMRect spread 는 own-enumerable 의존 (비표준 WebView 위험).
      // explicit destructure 로 cross-engine 안전성 확보.
      const rect = targetElem.getBoundingClientRect()
      if (rect.height === 0) {
        // F12: 선감싼 container 는 자식이 float 면 zoom 전까지 높이 0 — 자식으로 판정한다.
        const inner = targetElem.classList.contains('nf-zoom-container')
          ? (targetElem.firstElementChild?.firstElementChild as HTMLElement | null)
          : null
        if (inner == null || inner.getBoundingClientRect().height === 0) {
          // 자식도 높이 0(float 만 담은 wrapper 등)이면 zoom 대상이 아니다. 선감싼 container 는 풀고, 감싸지
          // 않았을 때처럼 그 안의 overflow 를 찾는다.
          if (inner != null && targetElem.zoomInfo?.ratio == null) {
            const elem = unwrapZoom(targetElem)
            if (elem != null) zoomCheck(elem)
          }
          return
        }
      }
      const oldRect = {
        left: rect.left - bodyRect.left,
        right: rect.right - bodyRect.left,
        x: rect.x - bodyRect.x,
        top: rect.top - bodyRect.top,
        bottom: rect.bottom - bodyRect.top,
        y: rect.y - bodyRect.y,
        width: rect.width,
        height: rect.height,
      }

      let availWidth = this.bodyWidth
      let curWidth = targetElem.offsetWidth

      let zoomInfo: ZoomInfo
      let container: HTMLElement
      let wrapper: HTMLElement
      let elem: HTMLElement
      let realWidth: number
      if (targetElem.getAttribute('data-nine-zoom-disabled') === 'true') return

      if (targetElem.classList.contains('nf-zoom-container') === false) {
        elem = targetElem
        if (
          elem.scrollWidth === 0 ||
          elem.scrollWidth < availWidth - 10 ||
          (elem.parentElement != null &&
            elem.parentElement.classList.contains('nf-zoom-wrapper') === true)
        ) {
          this.hideAllImgs(false)
          return
        }

        elem.setAttribute('data-nine-zoom-elem', 'true')
        zoomInfo = { originWidth: elem.scrollWidth, originHeight: elem.offsetHeight }

        container = createTag('<div class="nf-zoom-container"></div>')
        wrapper = createTag('<div class="nf-zoom-wrapper"></div>')
        container.zoomInfo = zoomInfo
        elem.zoomInfo = zoomInfo
        container.appendChild(wrapper)

        this.attachExpandBtn(container)
        elem.parentElement?.insertBefore(container, elem)
        wrapper.appendChild(elem)

        let curTmp: HTMLElement | null = container.parentElement
        while (
          curTmp != null &&
          curTmp.parentElement !== bodyElem &&
          curTmp.parentElement != null &&
          window.getComputedStyle(curTmp, null).display === 'inline'
        ) {
          curTmp = curTmp.parentElement
        }
        if (curTmp != null && curTmp.offsetWidth > 0) curWidth = curTmp.offsetWidth
        if (curWidth > 0) availWidth = Math.min(curWidth, availWidth)

        realWidth = Math.max(oldRect.width, zoomInfo.originWidth)
      } else {
        zoomInfo = targetElem.zoomInfo as ZoomInfo
        container = targetElem
        wrapper = container.firstElementChild as HTMLElement
        elem = wrapper.firstElementChild as HTMLElement
        // prewrapOverflowChildren 이 미리 붙인 버튼은 click handler 만 연결한다. layout 후 버튼을 새로
        // 붙이면 subtree 전체를 다시 layout 한다(요소 22만에서 약 5초).
        const prewrappedBtn = container.querySelector<ExpandBtnElement>(
          ':scope > .nf-removeZoomBtn',
        )
        if (prewrappedBtn == null) this.attachExpandBtn(container)
        else if (prewrappedBtn.__nineExpandHandler == null)
          this.bindExpandBtn(container, prewrappedBtn)

        // 생성 분기와 동일한 부모 폭 clamp 적용 — 적용/해제 판단의 availWidth를 대칭화. (R1)
        // 측정 전에 먼저 구해, 아래에서 컨테이너 폭을 현재 availWidth로 맞춰 재측정할 수 있게 한다.
        let curTmp2: HTMLElement | null = container.parentElement
        while (
          curTmp2 != null &&
          curTmp2.parentElement !== bodyElem &&
          curTmp2.parentElement != null &&
          window.getComputedStyle(curTmp2, null).display === 'inline'
        ) {
          curTmp2 = curTmp2.parentElement
        }
        if (curTmp2 != null && curTmp2.offsetWidth > 0) curWidth = curTmp2.offsetWidth
        if (curWidth > 0) availWidth = Math.min(curWidth, availWidth)

        // OMD-239: 재평가 전에 컨테이너 폭을 현재 availWidth로 맞춰 elem을 현재 폭 기준으로
        // 리플로우시킨다. 이 보정이 없으면 elem.scrollWidth가 래핑 당시(과거)의 넓은 폭을
        // 반영해, Safari resize 전이로 생긴 거짓양성이 영구히 해제되지 않는다(끈적임).
        container.style.width = availWidth + 'px'

        if (zoomInfo.originHeight !== elem.offsetHeight) zoomInfo.originHeight = elem.offsetHeight
        if (zoomInfo.originWidth !== elem.scrollWidth) zoomInfo.originWidth = elem.scrollWidth
        realWidth = zoomInfo.originWidth

        // 해제 임계. 아직 zoom 한 적 없는 선감싼 container(prewrapOverflowChildren, ratio==null)는
        // 생성 임계(-10px)로 판정한다 — 선감싸기가 inline px width 로만 고른 거짓양성을 layout 후 되돌린다.
        // 이미 zoom 된 container 는 OMD-239 규칙: 비-Safari(버퍼 0)는 -30px 히스테리시스(R1), Safari는
        // 생성 임계(zoomCheck: scrollWidth > bodyWidth + 버퍼)와 대칭이 되도록 availWidth + 버퍼로 해제해
        // 전이로 생긴 거짓양성(현재 폭 기준 realWidth ≤ +1)을 자동 치유한다. 진짜 wide 콘텐츠는 +3px↑(실측
        // +16px↑) 초과라 영향 없고, 대칭 경계라 생성/해제가 동시에 성립하는 구간이 없어 진동하지 않는다.
        const releaseThreshold =
          zoomInfo.ratio == null
            ? availWidth - 10
            : SAFARI_SCROLLW_BUFFER > 0
              ? availWidth + SAFARI_SCROLLW_BUFFER
              : availWidth - 30
        if (realWidth <= releaseThreshold) {
          const wasPrewrapped = zoomInfo.ratio == null
          const unwrapped = unwrapZoom(container)
          // 선감싼 container 는 부모 순회에서 이 자리를 건너뛰므로 안쪽 overflow 를 여기서 찾는다.
          if (wasPrewrapped && unwrapped != null) zoomCheck(unwrapped)
          return
        }
      }

      if (availWidth < (this.bodyWidth * 2) / 3) availWidth = (this.bodyWidth * 2) / 3

      const ratio = Math.min(1, availWidth / realWidth)
      zoomInfo.ratio = ratio

      this.minZoom = Math.min(ratio, this.minZoom)

      wrapper.style.transform = 'scale(' + ratio + ', ' + ratio + ')'
      const dir = window.getComputedStyle(wrapper, null).direction
      wrapper.style.transformOrigin = dir === 'rtl' ? 'right top' : 'left top'

      container.style.width = availWidth + 'px'
      container.style.height = zoomInfo.originHeight * ratio + 'px'
      container.style.overflow = 'hidden'

      this.zoomApplied = true

      if (this.applyZoomTimer != null) cancelAnimationFrame(this.applyZoomTimer)

      const itemElem = elem
      const containerElem = container
      this.applyZoomTimer = requestAnimationFrame(() => {
        const containerRect = containerElem.getBoundingClientRect()
        const checkCSSPosition = (item: HTMLElement): void => {
          if (item.getAttribute('data-nine-zoom-style') != null) return
          const itemRect = item.getBoundingClientRect()
          if (itemRect.top < containerRect.top || itemRect.bottom > containerRect.bottom) {
            // hideAllImgs(true)가 깜박임 마스킹용으로 임시 부여한 visibility:hidden 이 스냅샷에 굳으면,
            // removeZoom(removeZoomAttr)에서 이 스냅샷을 style 로 복원할 때 이미지가 다시 숨겨진다.
            // transient 마스킹 속성이므로 스냅샷에서 제외한다(원본 zoom 레이아웃 스타일만 보존).
            const oldStyle = (item.getAttribute('style') || '')
              .replace(/(?:^|;)\s*visibility\s*:[^;]*/gi, '')
              .replace(/^\s*;+/, '')
              .trim()
            item.setAttribute('data-nine-zoom-style', oldStyle)
            item.style.position = 'static'
            item.style.margin = 'unset'
          } else {
            const elems = item.children
            for (let i = 0; i < elems.length; i++) {
              checkCSSPosition(elems[i] as HTMLElement)
            }
          }
        }
        checkCSSPosition(itemElem)
        this.hideAllImgs(false)
      })
    }

    const zoomCheck = (elem: HTMLElement): void => {
      if (elem.offsetParent != null && elem.children.length > 0) {
        const elems = elem.children
        for (let i = 0; i < elems.length; i++) {
          const childEl = elems[i] as HTMLElement
          if (childEl.classList.contains('nf-zoom-container') === false) {
            // inline 요소에는 zoom 컨테이너를 생성하지 않는다.
            const computedStyle = window.getComputedStyle(childEl, null)
            const isNotInline = computedStyle.display !== 'inline'
            // Safari scrollWidth +1px 아티팩트를 무시하도록 가용 폭(bodyWidth)에 버퍼를 더한다.
            // 실제 overflow 대상은 +3px 이상 초과하므로 영향 없음. (OMD-239)
            if (
              childEl.scrollWidth === 0 ||
              childEl.scrollWidth > this.bodyWidth + SAFARI_SCROLLW_BUFFER
            ) {
              if (
                childEl.offsetHeight > 0 &&
                childEl.scrollWidth > this.bodyWidth + SAFARI_SCROLLW_BUFFER
              ) {
                const widthProp = getCSSProp(computedStyle.width)
                const minWidthProp = getCSSProp(computedStyle.minWidth)
                if (isNotInline && (widthProp.type === 1 || minWidthProp.type === 1)) {
                  if (childEl.offsetWidth > this.bodyWidth + SAFARI_SCROLLW_BUFFER) {
                    applyZoom(childEl)
                    continue
                  }
                }
              }
              // L1 — DOMRect spread 회피 (applyZoom 의 oldRect 와 동일 패턴).
              const childRect = childEl.getBoundingClientRect()
              const bound = {
                left: childRect.left - bodyRect.left,
                right: childRect.right - bodyRect.left,
                x: childRect.x - bodyRect.x,
                top: childRect.top - bodyRect.top,
                bottom: childRect.bottom - bodyRect.top,
                y: childRect.y - bodyRect.y,
                width: childRect.width,
                height: childRect.height,
              }

              if (
                childEl.offsetHeight > 0 &&
                elem !== bodyElem &&
                bound.x > this.bodyWidth / 3 &&
                bound.right > this.bodyWidth &&
                bound.width > 0
              ) {
                applyZoom(elem)
              } else if (
                childEl.tagName === 'TABLE' ||
                (isNotInline &&
                  childEl.offsetHeight > 0 &&
                  childEl.scrollWidth > 0 &&
                  childEl.scrollWidth === childEl.offsetWidth)
              ) {
                applyZoom(childEl)
              } else {
                zoomCheck(childEl)
              }
            } else if (childEl.querySelector('.nf-zoom-container') != null) {
              zoomCheck(childEl)
            }
          } else {
            applyZoom(childEl)
          }
        }
      } else {
        applyZoom(elem)
      }
    }

    const imgTags = bodyElem.querySelectorAll<HTMLImageElement>('img')
    for (let i = 0; i < imgTags.length; i++) {
      const imgListener = (e: Event): void => {
        this.hasHScroll()
        const imgElem = e.target as HTMLImageElement
        const zoomContainer = imgElem.closest<HTMLElement>('.nf-zoom-container')
        if (zoomContainer != null) zoomContainer.style.height = ''
        this.notifyResized()
      }

      const imgElem = imgTags[i]
      // 이미지의 width / height 가 절대 단위 (px 등) 로 명시되어 있는지 검사.
      // 둘 다 명시된 경우 → 로드 후 크기 변화가 없으므로 load listener 부착 불필요.
      // 하나라도 명시 안 됐거나 상대 단위 (%, em 등) 이면 로드 후 크기가 바뀔 수 있어 listener 부착.
      // (§7.1#5 widthProp.num + §7.4#4 heightProp dead path 정정 — 사용자 의도 확정)
      const isFixedSize = (styleValue: string, attrName: 'width' | 'height'): boolean => {
        let prop = getCSSProp(styleValue)
        if (prop.type !== 1) {
          const attrValue = imgElem.getAttribute(attrName)
          if (attrValue != null && attrValue.trim().length > 0) {
            prop = getCSSProp(attrValue.trim(), true)
          }
        }
        return prop.type === 1 && prop.isRel === false
      }
      const hasWidth = isFixedSize(imgElem.style.width, 'width')
      const hasHeight = isFixedSize(imgElem.style.height, 'height')

      // 재계산마다 리스너가 중복 누적되지 않도록 등록 전 기존 리스너를 제거하고,
      // 크기가 고정된 이미지는 로드되어도 크기가 변하지 않으므로 리스너를 해제한다.
      if (!hasWidth || !hasHeight) {
        if (imgTags[i].imgListener != null) {
          imgTags[i].removeEventListener('load', imgTags[i].imgListener as EventListener)
        }
        imgTags[i].imgListener = imgListener
        imgTags[i].addEventListener('load', imgListener)
      } else if (imgTags[i].imgListener != null) {
        imgTags[i].removeEventListener('load', imgTags[i].imgListener as EventListener)
        imgTags[i].imgListener = undefined
      }
    }

    if (this.hasHScroll() === true || this.zoomApplied === true) {
      this.minZoom = 1
      zoomCheck(bodyElem)
      const zoomContainer = bodyElem.querySelector('.nf-zoom-container')
      if (zoomContainer == null || this.zoomApplied !== true) this.hideAllImgs(false)
      this.showOptimizeBtn(this.minZoom < 1)
    } else {
      // F12: 선감쌌지만 overflow 가 없던 container 는 되돌린다 — 드문 경로, 그 subtree 만 재layout.
      bodyElem.querySelectorAll<HTMLElement>(':scope > .nf-zoom-container').forEach((c) => {
        if (c.zoomInfo?.ratio == null) {
          this.removePartialZoom(c, false, false)?.removeAttribute('data-nine-zoom-elem')
        }
      })
      this.showOptimizeBtn(false)
      this.hideAllImgs(false)
    }

    // OMD-239: resize 전이 직후엔 일부 요소가 변경 전(넓은) 폭으로 측정돼 거짓 래핑될 수 있다
    // (Safari sub-pixel 과측정이 아니라 전이 타이밍 문제 — 실측상 비결정적으로 발생).
    // 레이아웃이 안정화된 다음 프레임에 한 번 더 평가하면, 재사용 분기(현재 폭 재측정 + 대칭 해제)가
    // 전이로 생긴 거짓양성을 자동으로 풀어낸다. settle 패스 자신은 재예약하지 않는다(1회).
    if (isSettlePass === false && this.partialZoomMode === true && this.zoomApplied === true) {
      if (this.zoomSettleRaf != null) cancelAnimationFrame(this.zoomSettleRaf)
      this.zoomSettleRaf = requestAnimationFrame(() => {
        this.zoomSettleRaf = requestAnimationFrame(() => {
          this.zoomSettleRaf = null
          if (this.disposed === false && this.partialZoomMode === true) {
            this.applyPartialZoom(true)
          }
        })
      })
    }
  }

  onAdded(): void {
    setTimeout(() => {
      const hasHScroll = this.hasHScroll()
      // dev-emlview 하네스: 최적화(이미지 화면폭 축소)를 꺼 원본 크기로 렌더한다(raw 브라우저와 일치).
      const devNormal = win.__devDisableOptimize === true
      this.checkOption({ mode: devNormal ? 'normal' : 'optimized' })
      this.showOptimizeBtn(hasHScroll)
      if (!devNormal && win.NORMALIZE_MESSAGE_WIDTHS === true) {
        this.checkOption({ mode: 'optimized' })
      }
    }, 10)
  }

  notifyVisible(): void {
    const containers = this.container.querySelectorAll<HTMLElement>('.nf-zoom-container')
    containers.forEach((container) => {
      container.style.height = ''
    })
    // display none → visible 전환은 ResizeObserver에 기록되지 않으므로
    // prevWidth를 리셋해 becameVisible 가드가 확실히 동작하게 한다. (R6)
    if (this.observerObj != null) {
      this.observerObj.prevWidth = 0
    }
    this.notifyResized()
  }

  // 재계산 요청은 docUtil 레벨 통합 스케줄러로 모아서 처리한다. (R7)
  // 메일뷰별 개별 setTimeout(partialZoomTimer)은 제거됨.
  notifyResized(): void {
    if (this.partialZoomMode === true) {
      console.log(' -- notifyResized : ', Date.now())
      this.docUtil.requestZoom(this)
    } else {
      this.hideAllImgs(false)
    }
  }

  // 스케줄러가 호출하는 재계산 단일 진입점 — 서킷 브레이커(R4) + 재계산 + observer 동기화(R2).
  // 사용자 인터랙션/초기화 경로(setPartialZoom, setContainer)는 applyPartialZoom을
  // 직접 호출하므로 서킷 브레이커를 경유하지 않는다.
  runPartialZoom(): void {
    if (this.partialZoomMode !== true) return

    if (this.zoomSuspendedUntil != null) {
      if (Date.now() < this.zoomSuspendedUntil) return
      this.zoomSuspendedUntil = null
      this.zoomRunLog = []
    }

    const appliedBefore = this.zoomApplied
    const ratioBefore = this.minZoom

    console.log(' -- Resize~! : ', Date.now())
    this.applyPartialZoom()

    // 진동 신호 판정: 적용↔해제 전환은 항상 카운트, ratio 변화(>0.01)는 직전 변화와
    // 방향이 반전된 경우만 카운트. 단조 수렴(이미지 로드로 한 방향 이동)은 정상. (R4)
    let oscillating = appliedBefore !== this.zoomApplied
    const delta = this.minZoom - ratioBefore
    if (oscillating === false && Math.abs(delta) > 0.01) {
      oscillating = this.lastZoomDelta != null && delta > 0 !== this.lastZoomDelta > 0
      this.lastZoomDelta = delta
    }
    if (oscillating === true) {
      const now = Date.now()
      this.zoomRunLog = this.zoomRunLog.filter((t) => now - t < 10000)
      this.zoomRunLog.push(now)
      if (this.zoomRunLog.length >= 8) {
        console.warn('[mailview] partial zoom 진동 감지 - 30s 중단. key=', this.key)
        this.zoomSuspendedUntil = now + 30000
        // 중단 중 dirty 요청은 스케줄러 run()에서 소비·유실되므로 해제 시점의 재계산을 예약.
        if (this.zoomResumeTimer != null) clearTimeout(this.zoomResumeTimer)
        this.zoomResumeTimer = setTimeout(() => {
          this.zoomResumeTimer = null
          this.docUtil.requestZoom(this)
        }, 30000)
      }
    }

    // 재계산이 만든 크기 변화로 ResizeObserver가 재진입하지 않도록 observer 기준값을 동기화. (R2)
    // applyZoom 내부 rAF(checkCSSPosition) 이후 높이가 확정되므로 이중 rAF 뒤에 수행.
    // setContainer로 element가 교체된 경우엔 스킵 (이전 측정값을 새 element 기준에 오기록 방지).
    const syncTarget = this.observerObj != null ? this.observerObj.element : null
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          this.observerObj != null &&
          this.observerObj.element === syncTarget &&
          syncTarget != null
        ) {
          this.observerObj.prevWidth = syncTarget.offsetWidth
          this.observerObj.prevHeight = syncTarget.offsetHeight
        }
      })
    })
  }

  isZoomDisposed(): boolean {
    return this.disposed
  }

  setPartialZoom(value: boolean): void {
    if (value === true && this.smartViewMode === true) {
      throw new Error('smartView mode is already set.')
    }
    console.log(' - setPartialZoom : ', value)
    if (this.partialZoomMode === value) return

    this.partialZoomMode = value
    this.hideAllImgs(true)
    if (value === true) {
      this.applyPartialZoom()
    } else {
      this.removePartialZoom()
      this.hideAllImgs(false)
    }
  }

  private setActualView(value: boolean): void {
    const bodyElem = this.container
    if (value === true) {
      const imgTags = bodyElem.querySelectorAll<HTMLImageElement>('img')
      for (let i = 0; i < imgTags.length; i++) {
        const imgListenerAS = (_e: Event): void => {
          this.hasHScroll()
        }
        imgTags[i].imgListenerAS = imgListenerAS
        imgTags[i].addEventListener('load', imgListenerAS)
      }
    } else {
      const imgTags = bodyElem.querySelectorAll<HTMLImageElement>('img')
      for (let i = 0; i < imgTags.length; i++) {
        if (imgTags[i].imgListenerAS != null) {
          imgTags[i].removeEventListener('load', imgTags[i].imgListenerAS as EventListener)
        }
      }
    }
  }

  isOptimizeEnabled(): boolean {
    this.hasHScroll()
    return this.optimizeEnabled
  }

  setOptimized(value: boolean): void {
    this.setPartialZoom(value)
    this.setActualView(!value)
  }

  callWebview(param: string): void {
    if (this.nfCallback != null) this.nfCallback(param)
  }

  isModeEnabled(): boolean {
    const enabled = this.isOptimizeEnabled()
    setTimeout(() => {
      if (enabled === true) {
        this.callWebview('nine_mode_enabled')
      } else {
        this.callWebview('nine_mode_disabled')
      }
    }, 1)
    return enabled
  }

  setMode(mode: string): void {
    if (this.isOptimizeEnabled() !== true) {
      printLog('no need optimize.')
    } else if (mode === 'optimized') {
      this.setOptimized(true)
    } else {
      this.setOptimized(false)
    }
  }

  getMode(): 'none' | 'optimized' | 'normal' {
    if (this.isOptimizeEnabled() !== true) return 'none'
    if (this.partialZoomMode === true || this.smartViewMode) return 'optimized'
    return 'normal'
  }

  checkOption(opt?: MailViewOptions): void {
    const options: MailViewOptions = { uiBtn: true, mode: 'optimized' }
    if (opt?.mode != null) options.mode = opt.mode
    this.setOptions(options)
  }

  setBodyMargin(hMargin: string): void {
    this.container.style.setProperty('margin-left', hMargin, 'important')
    this.container.style.setProperty('margin-right', hMargin, 'important')
  }

  attachResizeEvent(): void {
    if (this.observerObj == null) {
      const observerEntry: ResizeObserverEntry = {
        element: this.container,
        prevWidth: -1,
        prevHeight: -1,
      }

      observerEntry.calcHeight = (): void => {
        if (observerEntry.resizeTimer != null) clearTimeout(observerEntry.resizeTimer)
        observerEntry.resizeTimer = setTimeout(() => {
          observerEntry.resizeTimer = null
          if (observerEntry.element != null) {
            const width = observerEntry.element.offsetWidth
            const height = observerEntry.element.offsetHeight
            const widthChanged = observerEntry.prevWidth !== width
            const becameVisible = observerEntry.prevWidth === 0 && width > 0
            // 재계산은 폭 변경/표시 전환 시에만. 높이만 변한 경우(콘텐츠 증가 등)는
            // 재계산 없이 네이티브 높이 통지만 수행한다. visible → hidden(폭 0)은 무시. (R6)
            if (width > 0 && (widthChanged || becameVisible)) {
              this.notifyResized()
            } else if (observerEntry.prevHeight !== height) {
              this.docUtil.notifyZoomHeightOnly()
            }
            observerEntry.prevWidth = width
            observerEntry.prevHeight = height
          }
        }, 300)
      }

      observerEntry.observer = new ResizeObserver(() => {
        observerEntry.calcHeight?.()
      })

      observerEntry.clear = (): void => {
        const { observer, resizeTimer } = observerEntry
        if (observer != null) {
          observer.disconnect()
          if (resizeTimer != null) clearTimeout(resizeTimer)
        }
      }

      observerEntry.start = (isFirstCall): void => {
        if (observerEntry.element != null) {
          if (isFirstCall === true) observerEntry.calcHeight?.()
          observerEntry.observer?.observe(observerEntry.element)
        }
      }

      this.observerObj = observerEntry
    } else {
      this.observerObj.clear?.()
      this.observerObj.element = this.container
      this.observerObj.prevWidth = -1
      this.observerObj.prevHeight = -1
    }

    if (this.doc.readyState === 'complete' || this.doc.readyState === 'interactive') {
      this.observerObj.start?.(true)
    } else {
      this.doc.addEventListener('DOMContentLoaded', () => {
        this.observerObj?.start?.(true)
      })
    }
  }

  setContainer(element: HTMLElement): void {
    this.observerObj?.clear?.()
    this.container = element
    if (this.observerObj != null) {
      this.observerObj.element = this.container
      // 이전 container의 기준값은 새 container에 적용하지 않음.
      this.observerObj.prevWidth = -1
      this.observerObj.prevHeight = -1
    }
    // 이전 container의 width 원본 정보는 새 container에 적용하지 않음.
    this.originBodyInfo = null
    if (this.partialZoomMode === true) this.applyPartialZoom()
    this.observerObj?.start?.()
  }

  // removeViewElement에서 호출. observer/타이머/이미지 리스너를 정리하여 누수를 방지.
  dispose(): void {
    // 멱등성 가드 — 이미 dispose된 경우 즉시 return.
    // (removeViewElement → clearAllViewElements 중복 경로에서 재정리 throw 방지)
    if (this.disposed === true) {
      return
    }
    this.disposed = true
    this.observerObj?.clear?.()
    this.docUtil.cancelZoom(this)
    if (this.zoomResumeTimer != null) {
      clearTimeout(this.zoomResumeTimer)
      this.zoomResumeTimer = null
    }
    if (this.applyZoomTimer != null) {
      cancelAnimationFrame(this.applyZoomTimer)
      this.applyZoomTimer = null
    }
    if (this.zoomSettleRaf != null) {
      cancelAnimationFrame(this.zoomSettleRaf)
      this.zoomSettleRaf = null
    }
    const imgTags = this.container.querySelectorAll<HTMLImageElement>('img')
    for (let i = 0; i < imgTags.length; i++) {
      if (imgTags[i].imgListener != null) {
        imgTags[i].removeEventListener('load', imgTags[i].imgListener as EventListener)
        imgTags[i].imgListener = undefined
      }
      if (imgTags[i].imgListenerAS != null) {
        imgTags[i].removeEventListener('load', imgTags[i].imgListenerAS as EventListener)
        imgTags[i].imgListenerAS = undefined
      }
    }
    // 미클릭 상태로 남은 확대버튼 click 리스너 해제. (R20)
    const expandBtns = this.container.querySelectorAll<ExpandBtnElement>('.nf-removeZoomBtn')
    for (let e = 0; e < expandBtns.length; e++) {
      const handler = expandBtns[e].__nineExpandHandler
      if (handler != null) {
        expandBtns[e].removeEventListener('click', handler)
        expandBtns[e].__nineExpandHandler = undefined
      }
    }
    this.container.mvObj = undefined
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile WebView legacy globals (uiWebview_*, isOfficeMailApp, etc.)
// ─────────────────────────────────────────────────────────────────────────────

interface UIWebViewState {
  resultCount: number
  foreColor: string
  backColor: string
}

const uiWebviewState: UIWebViewState = { resultCount: 0, foreColor: 'black', backColor: 'yellow' }

function uiWebview_HighlightAllOccurencesOfStringForElement(
  element: Node | null,
  keyword: string,
): void {
  if (element == null) return
  if (element.nodeType === 3) {
    let count = 0
    let elementTmp: Text = element as Text
    while (true) {
      const value = elementTmp.nodeValue ?? ''
      const idx = value.toLowerCase().indexOf(keyword)
      if (idx < 0) break
      count++
      elementTmp = document.createTextNode(value.substring(idx + keyword.length))
    }
    uiWebviewState.resultCount += count
    let index = uiWebviewState.resultCount
    let cursor: Text = element as Text
    while (true) {
      const value = cursor.nodeValue ?? ''
      const idx = value.toLowerCase().indexOf(keyword)
      if (idx < 0) break
      const span = document.createElement('span')
      const text = document.createTextNode(value.substring(idx, idx + keyword.length))
      span.appendChild(text)
      span.setAttribute('class', 'uiWebviewHighlight')
      span.style.backgroundColor = uiWebviewState.backColor
      span.style.color = uiWebviewState.foreColor
      index--
      span.setAttribute('id', 'SEARCH WORD' + index)
      const newText = document.createTextNode(value.substring(idx + keyword.length))
      cursor.deleteData(idx, value.length - idx)
      const next = cursor.nextSibling
      cursor.parentNode?.insertBefore(span, next)
      cursor.parentNode?.insertBefore(newText, next)
      cursor = newText
    }
  } else if (element.nodeType === 1) {
    const elemNode = element as HTMLElement
    if (elemNode.style.display !== 'none' && elemNode.nodeName.toLowerCase() !== 'select') {
      for (let i = elemNode.childNodes.length - 1; i >= 0; i--) {
        uiWebview_HighlightAllOccurencesOfStringForElement(elemNode.childNodes[i], keyword)
      }
    }
  }
}

function uiWebview_RemoveAllHighlightsForElement(element: Node | null): boolean {
  if (element == null) return false
  if (element.nodeType === 1) {
    const elem = element as HTMLElement
    if (elem.getAttribute('class') === 'uiWebviewHighlight') {
      const text = elem.removeChild(elem.firstChild as Node)
      elem.parentNode?.insertBefore(text, elem)
      elem.parentNode?.removeChild(elem)
      return true
    }
    let normalize = false
    for (let i = elem.childNodes.length - 1; i >= 0; i--) {
      if (uiWebview_RemoveAllHighlightsForElement(elem.childNodes[i])) normalize = true
    }
    if (normalize) elem.normalize()
  }
  return false
}

function uiWebview_RemoveAllHighlights(): void {
  uiWebviewState.resultCount = 0
  uiWebview_RemoveAllHighlightsForElement(document.body)
}

function uiWebview_HighlightAllOccurencesOfString(
  keyword: string,
  fColor: string | null,
  bColor: string | null,
): void {
  if (fColor != null) uiWebviewState.foreColor = fColor
  if (bColor != null) uiWebviewState.backColor = bColor
  uiWebview_RemoveAllHighlights()
  if (keyword == null || String(keyword).length === 0) {
    // 빈 검색어는 indexOf가 항상 0을 반환하여 무한루프가 발생함.
    return
  }
  uiWebview_HighlightAllOccurencesOfStringForElement(document.body, keyword.toLowerCase())
}

function uiWebview_ScrollTo(idx: number): void {
  const scrollTo = document.getElementById('SEARCH WORD' + idx)
  scrollTo?.scrollIntoView()
}

// iframe 컨텍스트에서만 글로벌 등록 — non-iframe 에서는 named export 로 직접 import 사용.
if (isIframe) {
  ;(globalThis as Record<string, unknown>).uiWebview_HighlightAllOccurencesOfString =
    uiWebview_HighlightAllOccurencesOfString
  ;(globalThis as Record<string, unknown>).uiWebview_RemoveAllHighlights =
    uiWebview_RemoveAllHighlights
  ;(globalThis as Record<string, unknown>).uiWebview_ScrollTo = uiWebview_ScrollTo
}

// ─────────────────────────────────────────────────────────────────────────────
// Bootstrap (named export — invoked by ViewerIFace)
// ─────────────────────────────────────────────────────────────────────────────

let mailViewInitialized = false

// inner mode 의 resetMailView() -> initMailView() 재진입 시 startMailView 의 지역 변수가
// 새로 생성되어 resize/load listener 가 host window 에 중복 등록되는 회귀 (viewer-refactor
// -task.md A5 / critic iter1 C-1) 회피용. 매 startMailView 호출 시 이전 handler 제거 후
// 새 handler 등록. handler closure 가 새 docUtil / checkHeight 를 참조하도록 함.
let currentResizeHandler: ((e: Event) => void) | null = null
let currentLoadHandler: (() => void) | null = null

export function _swapResizeListener(newHandler: (e: Event) => void): void {
  if (currentResizeHandler != null) {
    window.removeEventListener('resize', currentResizeHandler)
  }
  currentResizeHandler = newHandler
  window.addEventListener('resize', currentResizeHandler)
}

export function _swapLoadListener(newHandler: () => void): void {
  if (currentLoadHandler != null) {
    window.removeEventListener('load', currentLoadHandler)
  }
  currentLoadHandler = newHandler
  window.addEventListener('load', currentLoadHandler)
}

export function _resetListenerRefsForTest(): void {
  currentResizeHandler = null
  currentLoadHandler = null
}

function startMailView(
  isLoadedState: boolean,
  container?: HTMLElement | null,
  isDarkMode: boolean = false,
  darkmodeBackColor?: string | null,
  globalDarkMode?: boolean,
  darkModeTargetSelector?: string[] | null,
): void {
  if (document.body.style.visibility === 'hidden') {
    win._bodyBgColor = document.body.getAttribute('bgcolor')
    document.body.removeAttribute('bgcolor')
  }

  const docUtil = new DocUtil(document, callWebView, container)
  // iframe/mobile 은 분리 문서라 viewport meta 를 doc.head 에 주입한다(inner 는 host SPA 가 관리).
  // 기본 CSS(nf-* / overflow-wrap)는 양쪽 모두 CSS Modules(EmailContainer·EmailView style.module.scss)가
  // 담당하도록 이관해 setDefaultCSS 의 전역 `*` host 오염을 제거했다(§15.14-⑨/Q2).
  if (isIframe) docUtil.setViewMetaTag(true)
  docUtil.checkEditableElem()

  if (darkmodeBackColor != null) {
    docUtil.setDarkBackColor(darkmodeBackColor)
  }

  if (globalDarkMode === true) {
    docUtil.colorMgr.checkETVSelector = null
  } else {
    // host 가 명시한 다크 변환 대상 셀렉터가 있으면 EditorMgr 기본값을 override 한다(undefined 면 기본 유지).
    docUtil.colorMgr.checkETVSelector = [':where(.etv-container)', ':where(.etv-menu)']
    if (darkModeTargetSelector != null && darkModeTargetSelector.length > 0) {
      docUtil.colorMgr.checkETVSelector.push(...darkModeTargetSelector)
    }
  }

  window.docUtil = docUtil

  let lastHeight = 0
  let prevHeight = 0
  let timerH: number | null = null

  const notifyResize = (): void => {
    const sizeNotification = win.webkit?.messageHandlers?.sizeNotification
    if (sizeNotification != null) {
      sizeNotification.postMessage({
        width: document.body.scrollWidth,
        height: document.body.scrollHeight,
      })
    }
  }

  const enableResizeListener = (): void => {
    _swapResizeListener((e) => {
      printLog('resized - ', e)
      checkHeight(false, true)
    })
  }

  const checkHeight = (forced: boolean, isResize: boolean): void => {
    if (timerH != null) cancelAnimationFrame(timerH)
    timerH = requestAnimationFrame(() => {
      timerH = null
      const newHeight = document.documentElement.scrollHeight
      console.log(' -- checkHeight : ', newHeight, prevHeight, isResize)
      if (isResize === true) {
        // 창 리사이즈로 인한 재계산은 각 mailView container의 ResizeObserver가
        // 폭 변화를 감지해 처리하므로(R6/R8), 여기서는 전체 fan-out 없이
        // 네이티브 높이 통지만 수행한다.
        docUtil.colorMgr.nativeCall('resizeHeight', null, [newHeight])
        prevHeight = newHeight
      }
      printLog('check height - ' + newHeight)
      if (lastHeight === newHeight) {
        docUtil.setBodyVisible(true)
        // 강제 리렌더(FOUC 회피)용 body display 토글은 분리 문서(iframe/mobile) 전용. inner 에서
        // host body 를 껐다 켜면 앱 전체가 깜빡이므로 억제한다(뷰어 리렌더는 React 가 관리).
        if (forced === true && isIframe) {
          printLog('new rendering')
          document.body.style.display = 'none'
          requestAnimationFrame(() => {
            document.body.style.display = ''
          })
        }
        docUtil.callWebview('nine_rendering_finished')
        notifyResize()
        enableResizeListener()
      } else {
        lastHeight = newHeight
        checkHeight(false, false)
      }
    })
  }

  docUtil.checkHeight = checkHeight

  if (isLoadedState === true) {
    lastHeight = document.documentElement.scrollHeight
    printLog('lastHeight - ' + lastHeight)
    docUtil.callOnReady(isDarkMode)
    checkHeight(false, false)
  } else {
    // timeout이 먼저 실행된 뒤 load 이벤트가 도착하면 callOnReady가 중복 호출되므로 1회만 실행되도록 보장.
    let onReadyFired = false
    const fireOnReady = (forced: boolean): void => {
      if (onReadyFired === true) return
      onReadyFired = true
      lastHeight = document.documentElement.scrollHeight
      printLog('lastHeight - ' + lastHeight)
      docUtil.callOnReady(isDarkMode)
      checkHeight(forced, false)
    }

    const timerForLoading = setTimeout(() => {
      printLog('window load - timeout')
      fireOnReady(true)
    }, 1000)

    _swapLoadListener(() => {
      clearTimeout(timerForLoading)
      printLog('window load')
      fireOnReady(false)
    })
  }

  // §2c.4 + R22 — calendar event click 은 더 이상 document-level listener 가 아닌
  // EmailView 의 mailBody onClick (handleSchemaEventClick) 으로 처리한다.
  // iframe / inner mode 모두 React handler 로 일원화 — host document 에 등록되어
  // viewer 외 host UI 클릭까지 catch 하던 회귀 (R22) 가 구조적으로 차단된다.
}

/**
 * schema.org/Event 메타데이터를 가진 viewer DOM 클릭을 감지하여
 * docUtil.colorMgr.notifyCreateInvitation 으로 native 초대 생성 흐름을 trigger.
 *
 * iframe / inner mode 양쪽에서 EmailView 의 mailBody onClick 으로 호출되며,
 * mailviewForThread.js 시절 document-level click listener 의 자리를 대체한다.
 */
export function handleSchemaEventClick(target: EventTarget | null): void {
  if (!(target instanceof Element)) return

  let tbody: HTMLElement | null = null
  let item: HTMLElement | null = null
  let cursor: HTMLElement | null = target as HTMLElement
  do {
    tbody = cursor?.closest('tbody') ?? null
    if (tbody != null) {
      item = tbody.querySelector<HTMLElement>('[itemscope][itemprop="option"]')
      cursor = tbody.parentElement
    }
  } while (cursor != null && tbody != null && item == null)

  if (item != null && tbody === item.parentElement) {
    const itemType = item.getAttribute('itemtype')
    if (itemType === 'http://schema.org/Event') {
      const displayStr = readDisplayText(item)
      const startElem = item.querySelector('time[itemprop="startDate"]')
      const endElem = item.querySelector('time[itemprop="endDate"]')
      if (startElem != null && endElem != null) {
        const startDate = startElem.getAttribute('datetime')
        const endDate = endElem.getAttribute('datetime')
        if (startDate != null && endDate != null) {
          window.docUtil?.colorMgr.notifyCreateInvitation(startDate, endDate, displayStr)
          return
        }
      }
    }
  }

  const formTbody = (target as HTMLElement).closest<HTMLElement>(
    'input[value="itemtype-event"] + form',
  )
  if (formTbody != null) {
    const startElem = formTbody.querySelector('input[type="hidden"]:nth-of-type(1)')
    const endElem = formTbody.querySelector('input[type="hidden"]:nth-of-type(2)')
    if (startElem != null && endElem != null) {
      const displayStr = readDisplayText(formTbody)
      const startDate = startElem.getAttribute('value')
      const endDate = endElem.getAttribute('value')
      if (startDate != null && endDate != null) {
        window.docUtil?.colorMgr.notifyCreateInvitation(startDate, endDate, displayStr)
      }
    }
  }
}

// innerText 가 미정의인 환경 (jsdom 28 등) 에서도 안전하게 동작하도록 fallback.
function readDisplayText(elem: HTMLElement): string {
  return (elem.innerText ?? elem.textContent ?? '').trim()
}

/**
 * 명시적 초기화 진입점. ViewerIFace.initIFace 가 적절한 시점에 호출한다.
 * 중복 호출은 안전하게 무시된다.
 */
export function initMailView(
  container?: HTMLElement | null,
  isDarkMode: boolean = false,
  darkmodeBackColor?: string | null,
  globalDarkMode?: boolean,
  darkModeTargetSelector?: string[] | null,
): void {
  if (mailViewInitialized) return
  mailViewInitialized = true

  printLog('first readyState - ' + document.readyState)

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    printLog('readyState - complete')
    startMailView(
      document.readyState === 'complete',
      container,
      isDarkMode,
      darkmodeBackColor,
      globalDarkMode,
      darkModeTargetSelector,
    )
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      printLog('DOMContentLoaded.')
      startMailView(
        false,
        container,
        isDarkMode,
        darkmodeBackColor,
        globalDarkMode,
        darkModeTargetSelector,
      )
    })
  }
}

/**
 * InnerViewer 의 reload 패턴 (EmailContainer 강제 remount) 시 호출.
 *
 * `mailViewInitialized` once-guard 를 reset 하여 다음 `initMailView()` 호출 시
 * `startMailView` 가 재실행되도록 한다. iframe 모드는 `location.reload()` 가
 * JS context 자체를 reset 하므로 본 함수 불필요. inner mode 의 reload 동작 회귀
 * (stale DocUtil 이 destroyed DOM 의 bodyElem 참조) 회피용.
 *
 * 메모리 누수 측면 — 이전 DocUtil instance 의 event listener cleanup 은 별도
 * follow-up (docutil-resolver-task.md §11). 현재는 가드 reset 으로 회귀만 차단.
 */
export function resetMailView(): void {
  // 재진입 시 새 DocUtil이 생성되므로, 이전 인스턴스를 dispose 한다 — rootContainer 에 등록한
  // copy/cut 리스너 해제 + zoom 스케줄러 pending 타이머(분리된 MailView) 정리(§15.14-⑧ cleanup).
  window.docUtil?.dispose()
  mailViewInitialized = false
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile WebView legacy API — function declarations + iframe-only global registration
// ─────────────────────────────────────────────────────────────────────────────
//
// 다음 함수들은 모듈 내부 호출 / named export / iframe 글로벌 등록 세 경로 모두에서
// 일관되게 동작하도록 top-level function declaration 으로 정의. iframe 컨텍스트에서만
// `window` / `globalThis` 에 등록된다 (§8.2.2 글로벌 노출의 조건부 선언).

function isOfficeMailApp(): boolean {
  return window.navigator.userAgent.indexOf('OfficeMail') >= 0
}

function nineLog(msg: string): void {
  location.href = 'nine_log_?' + msg
}

function img_find(): string[] {
  const imgs = document.getElementsByTagName('img')
  const imgSrcs: string[] = []
  for (let i = 0; i < imgs.length; i++) imgSrcs.push(imgs[i].src)
  return imgSrcs
}

function reloadInlineImage(cid: string, uri: string): void {
  const images = document.getElementsByTagName('img')
  for (let i = 0; i < images.length; i++) {
    const image = images[i]
    if (image == null || image.src == null) continue
    const orgSrc = image.src
    if (orgSrc.indexOf(cid) === 0) image.src = uri
  }
}

// 글로벌 진입점 setDarkMode — host / native bridge 가 iframe.contentWindow.setDarkMode(...)
// 으로 호출. 내부 식별자가 DocUtil.setDarkMode 와 충돌하지 않도록 별도 이름.
function setGlobalDarkMode(backColor: string, foreColor: string): void {
  if (window.docUtil == null) {
    win._tmpDarkMode = { backColor, foreColor }
  } else {
    window.docUtil.setDarkForeColor(foreColor)
    window.docUtil.setDarkBackColor(backColor)
    window.docUtil.applyDarkMode()
    window.docUtil.setDarkMode(true)
  }
}

// iframe 컨텍스트에서만 글로벌 등록 — non-iframe 에서는 named export 로 직접 import 사용.
if (isIframe) {
  if (win.MailView == null) {
    win.MailView = MailView
  }
  win.isOfficeMailApp = isOfficeMailApp
  win.nineLog = nineLog
  win.img_find = img_find
  win.reloadInlineImage = reloadInlineImage
  win.setDarkMode = setGlobalDarkMode
}
// non-iframe consumer 를 위한 named exports.
export {
  isOfficeMailApp,
  nineLog,
  img_find,
  reloadInlineImage,
  setGlobalDarkMode,
  uiWebview_HighlightAllOccurencesOfString,
  uiWebview_RemoveAllHighlights,
  uiWebview_ScrollTo,
}

export type { NineNativeAPI }
